import type {
  AttentionQueueResponse,
  AttentionReason,
  CohortSnapshot,
  CohortSnapshotView,
  IQultureBadgeId,
  LifecycleStep,
  PipelineCardTag,
  TalentStageProgress,
} from '@tonnie/types';
/**
 * Business Service
 *
 * Business portal API methods.
 */

import type {
  AdaptationHealthBand,
  CarePillarHealth,
  OnTargetShareBand,
  CheckUpCalculationState,
  CitizenLevel,
  CitizenLevelLadder,
} from '@tonnie/heroes'; // L4 · F26
import type { PillarAnchorBand } from '@tonnie/types'; // L2 · F29
import type {
  AuditAction,
  AuditLogEntry,
  CheckupSharing,
  PipelineCardTag as PipelineSummaryTag,
  VettingPhaseStop,
  VettingRecency,
} from '@tonnie/types';
import type { HouseholdSummary } from '@tonnie/types'; // L5 · F35
import type { TaskPriority, TeleportalTaskStatus } from '@tonnie/types';
import { TonnieClient, type BlobWithFilename } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type {
  CreateCareInterventionInput,
  UpdateCareInterventionInput,
  ResolveCareInterventionInput,
  PoolBrowseSort,
  PoolSmartSearchRequest,
  CareInboxOrder,
  VettingStatusFilter,
  VettingBadgeFilter,
  MobilityListStatusFilter,
  MobilityTrackFilter,
  EvidenceStageFilter,
  AdaptationEngagementFilter,
  CareCommitmentFilter,
  CarePillarFilter,
  CareInterventionBatchInput,
  CareInterventionBatchResult,
  AdaptationCoachingRequest,
  AdaptationCoachingResult,
  NotStartedList,
  PoolMatchReason,
  PoolMatchBand,
  PoolCriteriaSummary,
  PoolSavedSearchCreate,
  PoolSavedSearchDto,
} from '@tonnie/schemas';
import type { CalculationTriggerResult, CheckUpResults } from './checkup.service';
import type {
  CareObjective,
  CareFeedbackCycle,
  CareObjectiveFeedbackEntry,
  SubmitObjectiveFeedbackInput,
} from './care.service';

// ═══════════════════════════════════════════════════════════════════════
// CATALOG TYPES (Sponsor Modal v2 / Radar Tooltips, 2026-Q3)
//
// Mirror the wire shapes produced by
// `apps/platform-api/src/modules/business/catalogs/catalog.types.ts`.
// Kept inline rather than imported from `@tonnie/types` because the SDK
// stays the single contract for the FE — apps depend on `@tonnie/sdk`
// only, never reach into platform-api types directly.
// ═══════════════════════════════════════════════════════════════════════

export interface ArchetypeCatalogEntry {
  code: string;
  name: string;
  description: string;
  persona: 'Native' | 'Expat' | 'Abroad';
}

export type ArchetypeCatalog = Record<string, ArchetypeCatalogEntry>;

export interface PillarScoringAnchor {
  min: number;
  max: number;
  desc: string;
  // ── L2 · F29 — the band as a code + position, never server prose ──
  band: PillarAnchorBand;
  // ── end L2 ──
}

export interface PillarCatalogEntry {
  code: string;
  name: string;
  description: string;
  evaluationQuestion: string;
  scoringAnchors: PillarScoringAnchor[];
}

export type PillarCatalog = Record<string, PillarCatalogEntry>;

// ═══════════════════════════════════════════════════════════════════════
// TEAM INVITATION TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface TeamInvitation {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  department: string | null;
  status: string;
  invitedBy: string;
  createdAt: string | null;
  expiresAt: string | null;
  acceptedAt: string | null;
  isExpired: boolean;
}

export interface InvitationInfo {
  id: string;
  email: string;
  firstName: string | null;
  role: string;
  status: string;
  tenantName: string;
  isExpired: boolean;
  expiresAt: string | null;
}

export interface AcceptInvitationResult {
  success: boolean;
  tenantId: string;
  role: string;
}

// ═══════════════════════════════════════════════════════════════════════
// TALENT TYPES
// ═══════════════════════════════════════════════════════════════════════

export type CanonicalPipelineStage =
  'acquisition' | 'vetting' | 'mobility' | 'integration' | 'care' | 'evidence';

export type StageStatus = 'on-track' | 'blocked' | 'at-risk' | 'completed';

/**
 * Snake-case keys for the 5 canonical CheckUp steps. Returned by
 * listTalents in `checkupMissingSteps`; consumed by the Pipeline
 * Vetting card tooltip.
 */
export type CheckUpStepKey =
  'videos_watched' | 'pitch_video' | 'language_tests' | 'motivational_answers' | 'assistant_chat';

export interface Talent {
  id: string;
  userId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  /** Raw DB pipeline_stage (legacy values like 'lead' may appear). */
  status: string;
  /** Canonical stage the Pipeline UI renders. */
  currentStage?: CanonicalPipelineStage;
  stageStatus?: StageStatus;
  /**
   * Parallel hero-engagement flags. Only meaningful when `currentStage`
   * is in a sponsored substage (mobility/integration/care/evidence) —
   * the backend gates them on stage at the SQL level. A sponsored
   * talent can be active in 0, 1, 2 or all 3 simultaneously, so the
   * Pipeline card renders one badge per active flag (no longer just
   * the single `currentStage` badge).
   */
  mobilityActive?: boolean;
  adaptationActive?: boolean;
  careActive?: boolean;
  /**
   * TelePortal track summary for the Mobility page's track cards / phase
   * counts / milestones / per-talent progress. `tracks[].id` is the track code
   * (`legal` | `travel` | `setup`); `progress` is the per-track %; the top-level
   * `progress` is the average across tracks (matches the citizen hero + the
   * Sponsored modal). Present only for talents with a TelePortal plan.
   */
  telePortal?: {
    progress: number;
    status: 'completed' | 'in_progress';
    blockedReason: string | null;
    tracks: Array<{ id: string; status: string; progress: number }>;
  };
  cohortId?: string | null;
  programId?: string | null;
  /** 0..100 — extracted from the cached `iqulture_score.overall` jsonb. */
  readinessScore?: number;
  iqultureScore?: number;
  currentCountry?: string | null;
  /** Residence city (list endpoint). Pairs with `currentCountry`. */
  currentCity?: string | null;
  /** Canonical upload-or-LinkedIn resolved headshot (list endpoint). */
  photo?: { url: string | null; source?: string | null } | null;
  /**
   * Journey route (ISO-3166-1 alpha-2). Detail endpoint (`getTalent`) only —
   * resolved from the talent's VISIBLE journey, so invited cross-tenant
   * talents carry their real origin/destination. Drives the sponsor modal's
   * Review-step route. Null on Pipeline list rows.
   */
  originCountry?: string | null;
  destinationCountry?: string | null;
  /** `null` per pillar when it was never scored — "not scored", never 0. */
  meshScores?: {
    mindset?: number | null;
    execution?: number | null;
    sustainability?: number | null;
    health?: number | null;
    overall?: number | null;
  } | null;
  /** ISO timestamp of the last meaningful update. */
  lastActivity?: string | null;
  lastActiveAt?: string;
  joinedAt?: string;
  createdAt?: string;
  /**
   * Abandonment marker. Set by the user.deleted Clerk webhook when
   * the citizen deletes their Tonnie account. The talent row stays
   * visible (its `deleted_at` is intentionally NOT stamped — that
   * would hide it from /api/business/talents) so the business can
   * see the historical record. Pipeline UI keys the "Citizen left
   * platform" badge off `deleteReason === 'user_account_deleted'`.
   * Null for healthy talents.
   */
  deleteReason?: string | null;

  /**
   * ISO timestamp of when the relationship left the live board — the
   * company's archive, else its end (the citizen left the programme, or
   * deleted the account). Set on the Archived tab; null on the active
   * kanban. Used by the Archived view to render "Archived Xd ago", to sort
   * by recency, and as the read-only discriminator.
   */
  archivedAt?: string | null;

  /**
   * When the citizen LEFT the company's programme (D1, 4 Oct 2026) — the
   * relationship ended. Drives the Archived card's "Left the programme" chip
   * and the drawer's banner. Null for every other state.
   */
  leftProgrammeAt?: string | null;

  /**
   * D2 — this company's request for more on the relationship (its CheckUp, or
   * more tests): waiting on the talent, or declined by them. Nothing widened
   * before their answer. Null when there is none, or once it was shared.
   */
  shareRequest?: PipelineShareRequest | null;

  /**
   * For archived rows: the canonical stage the candidate was IN
   * before being archived (e.g. `'vetting'`). Used by the Archived
   * tab on the Pipeline kanban to route each archived card to the
   * correct column instead of the `'acquisition'` fallback. Pulled
   * from `pipeline_transitions.from_stage` of the most recent
   * `to_stage='archived'` transition. Null for active rows.
   */
  preArchiveStage?: string | null;

  /**
   * Per-tenant journey id for this row. A single talent can have
   * multiple journey rows in one tenant (re-invitation cycles each
   * mint a fresh `journey_instances` row), so the Pipeline kanban
   * uses `journeyInstanceId` as its React reconciliation key on the
   * Archived tab — `talent.id` alone collides for talents with
   * multiple archived journeys.
   */
  journeyInstanceId?: string;

  // ── Profile fields surfaced by getTalent() (single-talent detail) ──
  // The Pipeline list endpoint doesn't return these — only the
  // detail endpoint (`getTalent(tenantId, talentId)`) populates them.
  // Both shapes share the same TS interface for caller convenience;
  // expect these fields to be `undefined` on list-page rows.
  /** Citizen phone number (digits only, no country code prefix). */
  phone?: string | null;
  /** ISO date string `YYYY-MM-DD`. Null until the citizen completes
   *  the onboarding wizard. */
  dateOfBirth?: string | null;
  /** Structured language proficiency from `talents.languages`. */
  languages?: Array<{ name: string; level: string }> | null;
  /** Free-form skill categories — JSONB from `talents.skills`. */
  skills?: Record<string, unknown> | null;
  linkedinUrl?: string | null;
  /**
   * Whether the citizen has shared anything about themselves — the Vetting
   * drawer shows its Profile tab only then (BIZ-DRAWER-10). Server-decided.
   */
  hasProfile?: boolean;
  /**
   * User-uploaded profile picture — from `users.avatar_url`. Populated
   * exclusively by `POST /api/users/me/avatar`. May hold an S3 key, an
   * S3 URL, or a `data:` URI; legacy rows JIT-provisioned before
   * migration 0058 may also hold an external Clerk URL. Detail endpoint
   * only — null on Pipeline list rows. Display logic prefers this field
   * and falls back to `clerkImageUrl`.
   */
  avatarUrl?: string | null;
  /**
   * Clerk-mirrored profile image — from `users.clerk_image_url`
   * (migration 0058). Always an external URL (typically
   * `https://img.clerk.com/...`), kept in sync with Clerk's
   * `imageUrl` on every webhook fire and the first AuthGuard pass.
   * Distinct from `avatarUrl` so a citizen who never uploaded but
   * signed in via Clerk still has a usable headshot in the Vetting
   * drawer. Detail endpoint only.
   */
  clerkImageUrl?: string | null;
  /**
   * The citizen-onboarding "bag of profile" JSONB
   * (`talents.custom_fields`). Populated by journey-web's onboarding
   * wizard. Known keys (each optional):
   *   - `address`: { street, city, state, country, postalCode }
   *   - `mobility`: { hasValidPassport, … } — citizenship is NOT here; read
   *     `citizenships`
   *   - `education`: Array<{ degree, field, institution, startDate, endDate }>
   *   - `experience`: Array<{ role, company, location, startDate, endDate, description }>
   *   - `skills`: { role?, languages?, hardSkills?, softSkills? }
   *   - `dateOfBirth`: same as the top-level field; preferred when present
   *   - `phoneCountryCode`: dial code (e.g. "PT")
   *   - `workModelPreference`: ('remote' | 'hybrid' | 'on-site')[]
   *   - `familyConfig`: { partner?, children?, pets? }
   *   - `websiteUrls`: Array<{ kind, url }>
   * Treated as `unknown`-typed at the SDK boundary; consumers cast
   * the leaves they need (see `buildTalentData` in the business-web
   * Talent Detail drawer for a reference reader).
   */
  customFields?: Record<string, unknown> | null;
  /**
   * Every citizenship, canonical first, resolved by the server — empty for a
   * redacted (departed) citizen. Detail endpoint only. Never re-derive it
   * from `customFields`.
   */
  citizenships?: string[];
  // ── L5 region: F35 household composition (server-classified; null = never answered) ──
  household?: HouseholdSummary | null;
  // ── end L5 region ──

  // ── Vetting card state (state-aware progress) ────────────────────
  // The Pipeline kanban renders ONE of four labels for talents in
  // Vetting: "Not started", "CheckUp X%", "Calculating iQulture…",
  // "iQulture XX% · BADGE". The discriminator + supporting fields
  // come from the backend so the FE never has to know the canonical
  // CheckUp step list (5 steps: videos / pitch / chat / languages
  // / motivational).
  vettingState?: 'not_started' | 'in_progress' | 'awaiting_score' | 'scored';
  /**
   * H20/BW-3 — the COMPANY-side axis, which `pipeline_stage` used to carry as
   * well as the citizen's progress.
   *
   *   not_invited → nobody has asked this talent for a CheckUp
   *   invited     → asked, citizen has NOT accepted yet
   *   started     → accepted and working through it
   *   completed   → submitted (scored, or awaiting the score)
   *
   * Server-derived. This field has existed on the FE type since the Vetting page
   * shipped and was never populated by the API — the modal synthesized it from
   * `currentStage`, i.e. from the conflation BW-4 removes.
   */
  /**
   * The lifecycle step — what this person is waiting on, spanning the invitation
   * funnel AND the CheckUp progression. Replaces `vettingInviteStatus`, which
   * reported `started` for a talent who had merely accepted.
   */
  lifecycleStep?: LifecycleStep;
  /** Why this row needs attention, as a CODE — the FE owns the words. */
  attentionReason?: AttentionReason | null;
  /**
   * The Evidence journey bar, computed on the server: four stages, each 0..100,
   * and their weighed `overall`. A stage the citizen has not shared with this
   * company is `null` ("not shared") and is left out of `overall`.
   */
  stageProgress?: TalentStageProgress;
  /**
   * May the company sponsor this talent now — the server's answer (the step is
   * decided in the talent's favour, and no sponsorship exists). Draw it; never
   * derive it from a badge or a status.
   */
  canSponsor?: boolean;
  /** The journey scopes this citizen does not share with the company. */
  withheldScopes?: string[];
  /**
   * May this company read the CheckUp's RESULT (D4): `shared`, or why not —
   * `awaiting_consent` (never shared) / `withdrawn`. The process (status,
   * steps, dates) is sent either way; score, badge and MESH only when shared.
   */
  checkupSharing?: CheckupSharing;
  /** A score exists — the process fact, never the score. */
  checkupScored?: boolean;
  /**
   * The card's recency line, named by the server: which moment and when
   * ("Scored …", "Completed …"). `null`: no line. List rows only.
   */
  vettingRecency?: VettingRecency | null;
  /**
   * Where the Vetting drawer's phase strip sits for this row, both tracks and
   * through the verdict (`vettingPhaseOfStep`); `null` once it left the pipeline.
   */
  vettingPhase?: VettingPhaseStop | null;
  /** ISO timestamp of the company's last CheckUp ask. Null if never asked. */
  vettingInvitedAt?: string | null;
  /**
   * BL-214 — TestIt invite progress. Present ONLY when the talent was invited
   * for "specific tests" (never for a full CheckUp). The Vetting card reads
   * this over `vettingState` so a completed-TestIt talent shows real progress
   * instead of "Not yet active". `done`/`total` = valid results vs invited tests.
   */
  testit?: { done: number; total: number; complete: boolean } | null;
  /** 'not_started' | 'in_progress' | 'completed' — raw DB status. */
  checkupStatus?: string | null;
  /** Number of CheckUp steps completed. Total = `checkupTotalSteps`. */
  checkupDoneSteps?: number;
  checkupTotalSteps?: number;
  /**
   * 0..100. Capped at 99 while in_progress so the bar never reaches
   * 100% before submission; flips to 100 when status='completed'.
   */
  checkupProgressPct?: number;
  /** ISO — when the citizen first opened the CheckUp. */
  checkupStartedAt?: string | null;
  /** ISO — when the citizen submitted the CheckUp for scoring. */
  checkupCompletedAt?: string | null;
  /** ISO — when the journey invitation was SENT (status flipped
   *  draft → pending). Returned ONLY by the single-talent detail
   *  endpoint (`getTalent`) — null on Pipeline list rows AND null
   *  for self-build journeys (no invitation row). This is the
   *  canonical anchor for "how long has this talent been waiting
   *  in vetting?" — strictly older than `acceptedAt`. */
  invitedAt?: string | null;
  /** ISO — when the citizen accepted the invitation (or the journey
   *  was activated, for self-build paths). Drives "Accepted X ago". */
  acceptedAt?: string | null;
  /** ISO — when the journey was activated. Equal to `acceptedAt` on
   *  invitation-driven journeys; for self-build journeys this is
   *  the invitation-less equivalent ("when did this candidate
   *  enter the funnel"). Detail endpoint only. */
  journeyActivatedAt?: string | null;
  /** ISO — when the citizen last logged in to Tonnie. Drives
   *  "Last seen X ago". Null until first session. */
  userLastSeenAt?: string | null;
  /** ISO — when the iQulture score landed. A date, sent whether or not the
   *  score is shared; null before scoring. */
  iqultureCalculatedAt?: string | null;
  /** Snake-case keys of CheckUp steps still pending. Empty when
   *  the citizen has completed all 5. Drives the in-progress
   *  vetting card tooltip ("Missing: Pitch video, Language tests"). */
  checkupMissingSteps?: CheckUpStepKey[];
  /** Origin and destination of the per-tenant journey. ISO-3166-1
   *  alpha-2 codes. Listed under these names by the API; the UI
   *  reads them via `journeyOriginCountry`/`journeyDestinationCountry`. */
  journeyOriginCountry?: string | null;
  journeyDestinationCountry?: string | null;
  jobTitle?: string | null;
  /** The server's iQulture badge id. Set when scored; draw it, never derive one. */
  iqultureBadge?: IQultureBadgeId | null;
  /** The score is PROVISIONAL — a lower bound, some evidence missing (Option B). Draw it beside the score. */
  iqultureProvisional?: boolean;
  /** What the complete evidence could reach; `null` unless provisional. */
  iqultureScoreMax?: number | null;
}

/**
 * Tighter, transport-faithful row shape returned by GET /business/talents.
 * Use this in the Pipeline kanban so the FE doesn't need `as unknown as`
 * casts. A superset of the historic `Talent` interface — keep both for
 * backward compatibility while consumers migrate.
 */
export interface PipelineRow extends Talent {
  /** Always present on list rows (per-tenant). */
  journeyInstanceId: string;
  /** Same as Talent, listed here for FE clarity. */
  checkupMissingSteps: CheckUpStepKey[];
  /**
   * The chips this card wears, decided server-side beside the lifecycle step.
   * (W7.1 · DW7-2)
   *
   * Non-optional on a list row on purpose: the front end no longer has a
   * derivation to fall back to, so a row without tags must be a compile error
   * here rather than a card that silently renders none.
   */
  cardTags: PipelineCardTag[];
  /**
   * `redacted` once the citizen deleted their account — the server's
   * projection, which every consumer reads instead of comparing a
   * `deleteReason` string. (F19)
   */
  identityState: 'present' | 'redacted';
  /**
   * Whether the company may ask this engagement for a full CheckUp or specific
   * tests. Decided server-side so every menu offers the same items. (F19)
   */
  canAskForAssessment: boolean;
  /**
   * Whether this Archived card may be activated again — never a relationship
   * the citizen left (D1) or whose account is gone. Server-decided, beside the
   * restore's own refusal.
   */
  canRestore: boolean;
}

export interface TalentListTotals {
  active: number;
  archived: number;
}

export interface PipelineListResponse {
  talents: PipelineRow[];
  /** Rows matching the VIEW that was asked for. */
  total: number;
  /**
   * Both tab badges, server-supplied. (W7.1 · DW7-1)
   *
   * Every tab-bearing surface renders "Active (N) | Archived (M)" while
   * fetching one view's rows. Five of them counted `array.length` over a page
   * of 20 for both numbers; the Pipeline, the one page that read `total`,
   * still paid two extra `limit: 1` round-trips to obtain the second.
   */
  totals: TalentListTotals;
}

export interface TalentFilters {
  status?: 'active' | 'inactive' | 'pending';
  cohortId?: string;
  programId?: string;
  pipelineStage?: string;
  search?: string;
  limit?: number;
  offset?: number;
  /**
   * Pipeline view selector — `'active'` (default) returns only live
   * journeys, `'archived'` flips to the Archived tab (admin-archived
   * AND citizen-abandoned, distinguishable via `deleteReason` on each
   * row), `'all'` returns both (admin reports).
   */
  view?: 'active' | 'archived' | 'all';
  /**
   * Resolve avatars? (W7.1)
   *
   * The API added `?photos=false` specifically for the surfaces that render no
   * face — the archived lists behind Evidence, Care and Mobility, and the
   * context backing every page's talent list — because a photo is ONE
   * concurrent S3 presign per row, which is why seven surfaces sat on a page
   * of 20. This type never carried the field, so the escape hatch the backend
   * documented was unreachable from any client and every one of those surfaces
   * kept paying for faces it does not draw.
   *
   * Absent means `true`, matching the controller: the board is the default
   * caller and it DOES render faces.
   */
  photos?: boolean;
}

export interface UpdateTalentInput {
  /** The journey a stage or cohort edit is for — required when the talent has several here. */
  journeyId?: string;
  firstName?: string;
  lastName?: string;
  pipelineStage?: string;
  cohortId?: string;
}

export interface UpdatePipelineStageInput {
  /** The journey relationship being moved — required when the talent has several here. */
  journeyId?: string;
  stage: string;
  notes?: string;
}

/** An interview before sponsoring, on one relationship. */
export interface RequestInterviewFirstInput {
  /** The journey relationship the interview is for — the drawer's. */
  journeyId?: string;
  providerName?: string;
  externalCandidateId?: string;
  notes?: string;
}

/** Acquisition → Vetting for one card. */
export interface TransitionTalentStageInput {
  /** The card being moved — required when the talent has several journeys here. */
  journeyId?: string;
  toStage: 'vetting';
  message?: string;
}

export interface PipelineTransition {
  id: string;
  talentId: string;
  fromStage: string;
  toStage: string;
  notes?: string;
  changedBy: string;
  createdAt: string;
}

export interface PipelineStageStats {
  name: string;
  count: number;
  trend: number;
}

export interface PipelineStats {
  stages: PipelineStageStats[];
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE + ENGAGEMENT (2026-Q3 hero engagement model)
// ═══════════════════════════════════════════════════════════════════════
//
// Two orthogonal axes describe a talent's position on the platform.
// The Business portal renders cohort/program/dashboard overviews from
// these two payloads so every surface stays mathematically consistent.
//
//   Phase (mutex)        — pending_invite | vetting | sponsored | archived
//   Engagement (parallel) — mobility / adaptation / care (only meaningful
//                           when phase = sponsored; a sponsored talent
//                           can be active in 0, 1, 2 or all 3 at once)
//
// Invariants the FE can rely on:
//   • pending_invite + vetting + sponsored === talentCount
//   • each engagement count ≤ phaseCounts.sponsored
//   • mobilityActive + adaptationActive + careActive MAY exceed
//     phaseCounts.sponsored — that's the parallel-engagement story.

export interface PhaseCounts {
  pending_invite: number;
  vetting: number;
  sponsored: number;
}

export interface EngagementCounts {
  mobilityActive: number;
  adaptationActive: number;
  careActive: number;
}

// ═══════════════════════════════════════════════════════════════════════
// PROGRAM TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface Program {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  startDate?: string | null;
  endDate?: string | null;
  cohortCount: number;
  talentCount: number;
  /** Talents in mobility/integration/care/evidence stages. */
  sponsoredJourneys: number;
  /**
   * 0..100 — average iQulture across the talents who SHARE their CheckUp
   * result; `null` ("—") when none does.
   */
  avgReadiness: number | null;
  /** Talents left out of this read because they withhold it from this company. */
  withheld: { checkup: number };
  /** 0..100 — % of talents who have reached evidence stage. */
  mobilityProgress: number;
  /** Mutex phase rollup. See PhaseCounts doc. */
  phaseCounts: PhaseCounts;
  /** Parallel engagement rollup. See EngagementCounts doc. */
  engagementCounts: EngagementCounts;
  /**
   * Pragmatic stage rollup for the program detail funnel. Only
   * `evidence` (= completed) is surfaced today; mobility / integration
   * / care are already covered by `engagementCounts` (parallel) and
   * `phaseCounts.sponsored` (mutex). FE reads `stageCounts.evidence`
   * directly instead of deriving it from `mobilityProgress`.
   */
  stageCounts?: { evidence: number };
  settings?: ProgramSettings;
  /** This program's OWN saved Care-strategy override (programs.settings.care), or
   * null when it inherits the company config. Hydrates the customize modal. */
  careStrategy?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ProgramSettings {
  maxTalents?: number;
  autoEnroll: boolean;
  requireApproval: boolean;
  modules: string[];
}

export interface CreateProgramInput {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  settings?: Partial<ProgramSettings>;
}

export interface UpdateProgramInput {
  name?: string;
  description?: string;
  status?: 'active' | 'paused' | 'completed' | 'archived';
  startDate?: string;
  endDate?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// COHORT TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface Cohort {
  id: string;
  /** Nullable: cohorts can exist standalone (migration 0041). */
  programId: string | null;
  /** Joined from `programs.name` server-side. Null when unassigned. */
  programName: string | null;
  name: string;
  description?: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  startDate?: string | null;
  endDate?: string | null;
  feedbackCadence?: 'bi-weekly' | 'monthly' | 'quarterly' | null;
  expectedTalentCount?: number | null;
  talentCount: number;
  /** 0..100 — % of talents in this cohort who reached evidence stage. */
  progress: number;
  /**
   * BL-073 — average iQulture across the cohort's talents who SHARE their
   * CheckUp result; `null` ("—") when none does.
   */
  avgReadiness: number | null;
  /**
   * Talents left out because they withhold it from this company: `checkup`
   * from `avgReadiness`, `qultureup` from `progress`.
   */
  withheld: { checkup: number; qultureup: number };
  /**
   * R1 plan · A4 — projected from hero signals (mobility blocked tasks,
   * rejected documents, overdue care checkpoints, escalated support
   * requests). Present on both list and detail responses.
   */
  blockedCount?: number;
  /**
   * R1 plan · A4 — soft signals: overdue mobility task or care
   * checkpoint due within 2 days. Mutex with `blockedCount` (a journey
   * already blocked is not double-counted in at-risk).
   */
  atRiskCount?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CohortDetail extends Cohort {
  /** Per-stage rollup (kept for back-compat with the Pipeline kanban). */
  stageCounts: Record<CanonicalPipelineStage, number>;
  /** Mutex phase rollup. See PhaseCounts doc. */
  phaseCounts: PhaseCounts;
  /** Parallel engagement rollup. See EngagementCounts doc. */
  engagementCounts: EngagementCounts;
  blockedCount: number;
  atRiskCount: number;
  /** This cohort's OWN saved Care-strategy override (cohorts.settings.care), or
   * null when it inherits. Hydrates the customize modal's override state. */
  careStrategy?: Record<string, unknown> | null;
}

export interface CreateCohortInput {
  /** Optional — see Cohort.programId. */
  programId?: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  feedbackCadence?: 'bi-weekly' | 'monthly' | 'quarterly';
  expectedTalentCount?: number;
}

export interface UpdateCohortInput {
  name?: string;
  description?: string;
  status?: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  startDate?: string;
  endDate?: string;
  feedbackCadence?: 'bi-weekly' | 'monthly' | 'quarterly';
  expectedTalentCount?: number;
}

// ═══════════════════════════════════════════════════════════════════════
// AFFILIATE TYPES (BL-213)
// ═══════════════════════════════════════════════════════════════════════

export interface AffiliateOverview {
  status: 'active' | 'paused';
  commissionRate: number;
  minimumPayout: number;
  currency: string;
  coupon: { code: string; percentOff: number; status: string } | null;
  totals: {
    redemptions: number;
    amountPaid: number;
    discountGiven: number;
    commission: number;
  };
  balance: number;
  series: Array<{ date: string; redemptions: number; commission: number }>;
  /**
   * What a customer pays for Pro (monthly) with this affiliate's coupon —
   * priced by the API with checkout's own rule, beta stacking included. All
   * amounts MAJOR units in `currency`. `null` when there is no coupon or it
   * does not apply to subscriptions. The page renders it; it never computes it.
   */
  checkoutPreview: {
    planCode: 'pro_monthly';
    currency: string;
    /** Catalogue price per month. */
    listPrice: number;
    /** Charged before the coupon — the beta promo price while beta pricing runs. */
    basePrice: number;
    /** The coupon checkout attaches (combined with the platform's in percentage mode). */
    percentOff: number;
    /** What the customer pays per month. */
    finalPrice: number;
  } | null;
}

/**
 * Tonnie-wide cross-tenant affiliate overview (B10). Aggregates PAID redemptions
 * across ALL issuer tenants — the platform (Tonnie-staff) view, distinct from the
 * per-tenant `AffiliateOverview`. Restricted server-side to the Tonnie internal
 * tenant + audit-logged.
 */
export interface AffiliatePlatformOverview {
  currency: string;
  totals: {
    redemptions: number;
    amountPaid: number;
    discountGiven: number;
    commission: number;
  };
  issuers: Array<{
    issuerTenantId: string;
    issuerName: string;
    redemptions: number;
    amountPaid: number;
    discountGiven: number;
    commission: number;
  }>;
}

/** Chart aggregation bucket. */
export type AffiliateSeriesGranularity = 'day' | 'month' | 'year';
/** Anonymized B2C purchase scenario. */
export type AffiliateScenario = 'unlock' | 'renewal' | 'pro' | 'other';

export interface AffiliateRedemption {
  id: string;
  product: string | null;
  /** Anonymized purchase scenario derived server-side from `product`. */
  scenario: AffiliateScenario;
  /** Best-effort route (ISO alpha-2) from the redeemer's primary journey. */
  originCountry: string | null;
  destinationCountry: string | null;
  amountPaid: number;
  discountGiven: number;
  commission: number;
  status: 'paid' | 'pending' | 'refunded';
  createdAt: string;
}

export interface AffiliateRedemptionList {
  items: AffiliateRedemption[];
  total: number;
  page: number;
  limit: number;
}

/** Chart series filters (drive the overview `series`) — same set as the list. */
export interface AffiliateOverviewParams {
  granularity?: AffiliateSeriesGranularity;
  from?: string;
  to?: string;
  scenario?: AffiliateScenario;
  status?: 'paid' | 'pending' | 'refunded';
}

/** Redemption list + export filters (range / scenario / status). */
export interface AffiliateRedemptionParams {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
  scenario?: AffiliateScenario;
  status?: 'paid' | 'pending' | 'refunded';
}

/** Export filters — same set as the list, without pagination. */
export type AffiliateExportParams = Omit<AffiliateRedemptionParams, 'page' | 'limit'>;

export interface AffiliatePayout {
  id: string;
  amount: number;
  status: 'requested' | 'approved' | 'invoice_received' | 'paid' | 'rejected' | 'cancelled';
  hasInvoice: boolean;
  paidAt: string | null;
  createdAt: string;
}

/** Result of `POST /api/business/pool/import` (BL-207). */
export interface PoolImportResult {
  created: number;
  updated: number;
  skipped: number;
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════
// POOL TYPES (frontend-facing field names)
// ═══════════════════════════════════════════════════════════════════════

export interface PoolTalent {
  id: string;
  name: string;
  email: string;
  /** City. Backend column is `current_city` but frontend reads `location`. */
  location: string;
  /** Country. Backend column is `current_country`. */
  originCountry: string;
  /** Job title / current role (from the CV, when parsed). */
  role?: string;
  skills: string[];
  /** 0..100 — flattened from talents.iqulture_score.overall. */
  readinessScore: number;
  iqultureScore: number;
  /** The server's badge for that score; `null` when the person was never scored. */
  iqultureBadge: IQultureBadgeId | null;
  /** The score is PROVISIONAL — a lower bound, some evidence missing (Option B). Draw it beside the score. */
  iqultureProvisional: boolean;
  /** What the complete evidence could reach; `null` unless provisional. */
  iqultureScoreMax: number | null;
  availability: 'available' | 'in_consideration' | 'unavailable';
  /** Vault file id of the candidate's CV when one was ingested (BL-207). */
  cvFileId?: string | null;
  /**
   * Already in THIS company's pipeline — a live journey it can see, or an
   * outstanding invitation to the candidate. The server's answer (P2.1).
   */
  inPipeline: boolean;
  source?: 'tonnie' | 'external';
  /** When `source = 'external'` only. */
  externalProviderId?: string;
  externalProviderName?: string;
  externalProviderLogoUrl?: string;
}

export interface PoolListResponse {
  talents: PoolTalent[];
  total: number;
}

/**
 * Pool overview (BL-207) — the whole-pool baseline the summary cards read:
 * `total` (full searchable pool), `avgReadiness` (mean iQulture over ASSESSED
 * candidates only), and a rich-first `talents` sample for facet/role fallbacks.
 */
export interface PoolOverviewResponse extends PoolListResponse {
  /** Mean iQulture (0..100) across candidates who have a CheckUp; 0 if none. */
  avgReadiness: number;
  /** How many of the WHOLE pool are already in this company's pipeline. */
  inPipeline: number;
}

/**
 * A single pool candidate's full profile (BL-207 detail drawer). Extends the
 * list shape with the structured metadata extracted at import time. Every extra
 * field is optional/nullable — populated only when the source CV/roster carried
 * it — and the drawer renders each section only when present.
 */
/**
 * A company's request for more on a relationship (D2), as its card and drawer
 * say it: "Asked for the CheckUp — waiting for the talent", or "declined".
 */
export interface PipelineShareRequest {
  status: 'pending' | 'declined';
  /** Their CheckUp, or specific TestIt tests. */
  asks: 'checkup' | 'tests';
  /** When it was asked (pending) or answered (declined). ISO. */
  at: string;
}

export interface PoolTalentDetail extends PoolTalent {
  /** ISO country code the candidate holds citizenship of, when known. */
  nationality?: string;
  /** e.g. "Senior", "Lead" — from the CV when parsed. */
  seniorityLevel?: string;
  /** Total years of professional experience, when parsed. */
  yearsExperience?: number | null;
  /** Display strings, e.g. ["English (Fluent)", "Portuguese"]. */
  languages?: string[];
  linkedinUrl?: string;
  /** ISO timestamp the candidate was added to the pool. */
  poolImportedAt?: string | null;
}

/** A pool candidate ranked against a Job Description (BL-207 smart search). */
export interface PoolSmartSearchResult extends PoolTalent {
  /** 0..100 blended relevance to the JD (semantic + skills + readiness). */
  matchScore: number;
  /** The server's band for that score — the card colours it. */
  matchBand: PoolMatchBand;
  /** Structured, i18n-safe reasons — the FE renders each via `t()`. */
  matchReasons: PoolMatchReason[];
  /** Vault file id of the candidate's CV, when one was ingested; else null. */
  cvFileId: string | null;
}

export interface PoolSmartSearchResponse {
  talents: PoolSmartSearchResult[];
  total: number;
  page: number;
  limit: number;
  /** The criteria the AI extracted from the JD — shown/edited in the wizard. */
  criteria: PoolCriteriaSummary;
}

// Re-export the shared smart-search request/reason/criteria types so pool
// callers get the whole contract from `@tonnie/sdk` without a second import.
export type {
  PoolBrowseSort,
  PoolSmartSearchRequest,
  PoolMatchReason,
  PoolMatchBand,
  PoolCriteriaSummary,
  PoolSavedSearchCreate,
  PoolSavedSearchDto,
};

/** Outcome of a bulk pool→Acquisition add (BL-207) — partial success allowed. */
export interface PoolBulkAddResult {
  // Each added candidate becomes a DRAFT invitation in Acquisition (BL-207).
  added: Array<{ talentId: string; invitationId: string }>;
  /**
   * Candidates already invited to this cohort. Invitations are idempotent on
   * (tenant, email, cohort), so re-adding returns the EXISTING invitation —
   * these are neither adds nor failures, and counting them as adds told the
   * admin they had brought in candidates they already had.
   */
  duplicates: Array<{ talentId: string; invitationId: string }>;
  failed: Array<{ talentId: string; reason: string }>;
}

/** A pool facet field the Search-tab dropdowns type-ahead against (BL-207). */
export type PoolFacetField = 'skills' | 'roles' | 'locations';

// ═══════════════════════════════════════════════════════════════════════
// ANALYTICS TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface AnalyticsSummary {
  totalTalents: number;
  activeTalents: number;
  activePrograms: number;
  /** Over the talents who share their CheckUp result; `null` when nobody is scored. */
  averageMeshScore: number | null;
  completionRate: number;
  engagementRate: number;
  /** Talents left out of this read because they withhold it from this company. */
  withheld: { checkup: number };
}

/**
 * The company's MESH population — one representative CheckUp per talent who
 * SHARES their CheckUp result. Each mean is `null` when nobody is scored on it,
 * with its own denominator in `pillarCounts`.
 */
export interface MeshAnalyticsSummary {
  averageScores: {
    mindset: number | null;
    execution: number | null;
    sustainability: number | null;
    health: number | null;
    overall: number | null;
  };
  pillarCounts: { mindset: number; execution: number; sustainability: number; health: number };
  talentsAssessed: number;
  distribution: { low: number; medium: number; high: number };
  /** Talents left out because they withhold their CheckUp result. */
  withheld: { checkup: number };
}

export interface AnalyticsTimeSeries {
  period: 'day' | 'week' | 'month';
  data: Array<{
    date: string;
    value: number;
    label?: string;
  }>;
}

export interface AnalyticsFilters {
  programId?: string;
  cohortId?: string;
  startDate?: string;
  endDate?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// TENANT & AUDIT TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface TenantSettings {
  tenantId: string;
  companyName?: string | null;
  companySize?: string | null;
  industry?: string | null;
  website?: string | null;
  headquarters?: string | null;
  branches?: string[];
  targetMarkets?: string[];
  hiringGoals?: Record<string, unknown>;
  useCases?: string[];
  preferences?: Record<string, unknown>;
}

/**
 * Response from POST /api/business/onboarding/enrich
 *
 * The server merges Brandfetch, the BRAIN LLM extractor, and heuristic
 * providers into a single view. `confidence` is a 0..1 weighted fill-rate
 * the frontend can use to decide whether to show an "auto-filled" badge.
 */
export interface EnrichedCompanyData {
  name: string | null;
  description: string | null;
  industry: string | null;
  companySize: string | null;
  foundedYear: number | null;
  logoUrl: string | null;
  linkedinUrl: string | null;
  registeredCountry: string | null;
  hqCountry: string | null;
  hqCity: string | null;
  hqTimezone: string | null;
}

export interface EnrichResponse {
  confidence: number;
  source: 'brandfetch' | 'llm' | 'composite' | 'cache' | 'heuristic' | 'none';
  cached: boolean;
  data: EnrichedCompanyData;
}

// ═══════════════════════════════════════════════════════════════════════
// OBJECTIVES-BASED FEEDBACK (BL-125)
//
// Mirrors the business-facing wire shapes of the Care objective-feedback
// service. The objective/cycle/entry row types are owned by `care.service`.
// ═══════════════════════════════════════════════════════════════════════

/**
 * One JOURNEY on the HR "objective feedback due" list — each journey's
 * objectives run their own cycle (X7), so a person on two journeys with the
 * company can be due twice.
 */
export interface ObjectiveFeedbackDueTalent {
  talentId: string;
  journeyInstanceId: string;
  displayName: string;
  email: string;
  cycleId: string;
  status: string;
  dueAt: string;
}

export interface ObjectiveFeedbackDueResponse {
  talents: ObjectiveFeedbackDueTalent[];
  /** Talents left out because they withhold Care from this company. */
  withheld: { care: number };
}

/**
 * Business read model: objectives + recent feedback cycles (newest first) + all
 * their entries. The OPEN cycle (to submit against) is
 * `cycles.find(c => c.status === 'open' || c.status === 'provider_submitted')`;
 * the remaining cycles are the history pulse.
 */
export interface TalentObjectivesView {
  /**
   * `false` → the citizen does not share Care with this company: the arrays
   * are empty because nothing was read, not because there is nothing.
   */
  entitled: boolean;
  objectives: CareObjective[];
  cycles: CareFeedbackCycle[];
  feedback: CareObjectiveFeedbackEntry[];
  /**
   * BL-122 — the EFFECTIVE Care visibility for the caller on this read. The
   * backend has already redacted the free text (and flagged each entry it
   * withheld); this only tells the UI which empty state to draw.
   */
  visibility: { canSeeDetailedFeedback: boolean };
  /**
   * Whether this company gives provider feedback on the talent at all — it does
   * when its Care strategy governs the talent. Another company engaged on the
   * journey reads the citizen's self-evaluations, never provides (0190).
   */
  canProvideFeedback: boolean;
}

/** One objective in the company achievement rollup. */
export interface ObjectivesOverviewItem {
  catalogObjectiveId: string;
  title: string;
  category: string | null;
  measureType: string;
  talentCount: number;
  withDataCount: number;
  onTargetCount: number;
  /** Share (0–100) of talents with data who are on target, and its band; `null` with none. */
  onTargetShare: { pct: number; band: OnTargetShareBand } | null;
  avgProgress: number | null;
}

/** Company-wide objectives achievement dashboard. */
export interface CareObjectivesOverview {
  summary: {
    objectivesTracked: number;
    talentsTracked: number;
    onTrackPct: number | null;
  };
  objectives: ObjectivesOverviewItem[];
  /** Talents left out of the rollup because they withhold Care. */
  withheld: { care: number };
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class BusinessService {
  constructor(private readonly client: TonnieClient) {}

  // ─── Catalogs (Radar Tooltips, 2026-Q3) ────────────────────────────
  // Static-ish dictionaries surfaced by `GET /api/business/catalogs/*`.
  // The platform-api proxy unwraps Brain's envelope and flattens the
  // persona buckets into `name → entry` (archetypes) / `code → entry`
  // (pillars) so the radar hover-explainers can do O(1) lookup per
  // axis with no further massaging. Both endpoints are tenant-aware:
  // per-tenant Brain config overrides resolve through `@CurrentTenant`.

  /**
   * Fetch the Cultural Risk Archetype catalog for the caller's tenant.
   * Used by `TalentDetailVetting` to populate the Archetypes radar
   * hover bubbles with Brain's canonical profile definitions.
   */
  async getArchetypeCatalog(): Promise<ArchetypeCatalog> {
    return this.client.get<ArchetypeCatalog>('/api/business/catalogs/archetypes');
  }

  /**
   * Fetch the Evaluation Pillar catalog for the caller's tenant. Each
   * entry carries Brain's description + evaluation question + the
   * scoring anchors (each with a derived band code + position) that
   * drive the at-this-score callout in the Pillars radar hover bubble.
   */
  async getPillarCatalog(): Promise<PillarCatalog> {
    return this.client.get<PillarCatalog>('/api/business/catalogs/pillars');
  }

  // Talent Methods
  /**
   * Get talents — list endpoint that powers the Pipeline kanban.
   * Returns the tighter `PipelineListResponse` shape so callers don't
   * need to cast or unwrap.
   */
  async getTalents(filters?: TalentFilters): Promise<PipelineListResponse> {
    return this.client.get<PipelineListResponse>(API_ENDPOINTS.BUSINESS.TALENTS, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The attention queue — ranked server-side. (W7.2 · F28)
   *
   * `limit` is required at this boundary for the same reason `view` and
   * `limit` are on the list hook: three components each sliced this queue to a
   * different hardcoded number, and a default here would simply be a fourth.
   */
  async getAttentionQueue(params: {
    limit: number;
    programId?: string;
    cohortId?: string;
  }): Promise<AttentionQueueResponse> {
    return this.client.get<AttentionQueueResponse>(API_ENDPOINTS.BUSINESS.ATTENTION_QUEUE, {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The cohort SNAPSHOT — the roster the server already assembles. (W7.1 · F19)
   *
   * The endpoint has existed since W3 and nothing called it: six constants sat
   * in `client/config.ts` with no method and no hook, while the cohort page
   * built a parallel mapper over `getTalents` that de-duplicated by person and
   * labelled the phase with seven hardcoded English strings. This is the
   * method that was missing.
   */
  async getCohortSnapshot(
    cohortId: string,
    params: {
      limit: number;
      offset?: number;
      lifecycleStep?: string;
      /**
       * The tab. (F22) `active` / `archived` cut the roster server-side and the
       * response's `counts` badges both tabs; omitted, the roster is the whole
       * cohort — what a machine client and every export read.
       */
      view?: CohortSnapshotView;
    }
  ): Promise<CohortSnapshot> {
    // Built from `COHORTS` rather than from a per-route constant — the same
    // shape `downloadCohortReportCsv` uses two methods below. The dedicated
    // `COHORT_SNAPSHOT` constant existed with no consumer for a whole release
    // and was removed as dead; a constant nothing calls is not the contract,
    // the method is.
    return this.client.get<CohortSnapshot>(
      `${API_ENDPOINTS.BUSINESS.COHORTS}/${cohortId}/snapshot`,
      { params: params as Record<string, string | number | boolean | undefined> }
    );
  }

  /**
   * Get talent by ID. Optional `journeyId` disambiguates which journey
   * row to load when a talent has multiple alive journeys in the same
   * tenant (multi-opportunity).
   */
  async getTalent(id: string, options?: { journeyId?: string }): Promise<Talent> {
    return this.client.get<Talent>(`${API_ENDPOINTS.BUSINESS.TALENTS}/${id}`, {
      params: options?.journeyId ? { journeyId: options.journeyId } : undefined,
    });
  }

  /**
   * Bulk-archive a batch of JOURNEYS in one call. Each journey goes
   * through the canonical archive flow; the call returns per-journey
   * outcomes rather than failing if one row is missing.
   *
   * Addressed by journey id, like the single-journey archive: a talent
   * can hold several live journeys in one tenant, so a talent-id batch
   * could not express "archive this card and not that one".
   */
  async bulkArchiveJourneys(input: { journeyIds: string[]; reason?: string }): Promise<{
    archived: number;
    failed: number;
    results: Array<{ journeyId: string; success: boolean; error?: string }>;
  }> {
    return this.client.post(API_ENDPOINTS.BUSINESS.JOURNEYS_BULK_ARCHIVE, input);
  }

  /**
   * Bulk-sponsor a batch of talents into the same opportunity.
   */
  async bulkSponsorTalents(input: {
    talentIds: string[];
    /** `journeyIds[i]` is `talentIds[i]`'s relationship — needed for a talent with several. */
    journeyIds?: string[];
    cohortId?: string;
    programId?: string;
    branchName?: string;
    workLocation?: string;
    pricingPlan?: 'free' | 'enterprise';
    durationMonths?: number;
  }): Promise<{
    sponsored: number;
    failed: number;
    results: Array<{ talentId: string; success: boolean; journeyId?: string; error?: string }>;
  }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.TALENTS}/bulk-sponsor`, input);
  }

  /**
   * Single-stage transition driver. Used by drag-and-drop. Currently
   * only Acquisition → Vetting is supported here; Sponsor stays on its
   * dedicated endpoint because it requires the full opportunity payload.
   */
  async transitionTalentStage(
    talentId: string,
    input: TransitionTalentStageInput
  ): Promise<{ success: true; talentId: string; fromStage: string; toStage: 'vetting' }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/stage`, input);
  }

  /**
   * Invite talent (single).
   */
  async inviteTalent(input: {
    email: string;
    firstName?: string;
    lastName?: string;
    programId?: string;
    cohortId?: string;
  }): Promise<{
    success: boolean;
    message: string;
    talentId: string;
    alreadyExists?: boolean;
  }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.TALENTS}/invite`, input);
  }

  /**
   * Move a batch of talents into the `vetting` stage and dispatch
   * the `vetting.invite` system template.
   */
  async inviteTalentsToVetting(input: {
    talentIds: string[];
    /** The relationships the ask is for — the selected cards; needed for a talent with several. */
    journeyIds?: string[];
    /** The operator's note — it rides in whichever message the ask turns out to need. */
    message?: string;
    /** BL-214 — full CheckUp (default) vs a TestIt "specific tests" invite. */
    invitationKind?: 'full_checkup' | 'testit' | null;
    /** TestIt: chosen reusable-test catalog ids (required when kind='testit'). */
    testitTests?: string[] | null;
    /** TestIt: optional tighter reuse-freshness window in days. */
    maxResultAgeDays?: number | null;
  }): Promise<{
    invited: number;
    errors: Array<{ talentId: string; error: string }>;
    /**
     * Talents whose stage transition committed but whose invitation email could
     * not be dispatched. They are counted in `invited` — the pipeline did move —
     * so the UI must report this separately, or the operator waits on a reply
     * that can never come.
     */
    emailFailures: Array<{ talentId: string; email: string; error: string }>;
    /**
     * How many stayed in Acquisition because they have not accepted yet (BW-4).
     *
     * NOT an error — it is the correct outcome. The endpoint has computed it
     * since BW-4 landed, with a docblock saying it exists so the UI can say
     * "invited, awaiting acceptance" instead of implying everyone moved to
     * Vetting; it was simply never surfaced in the type, so no caller could
     * read it and the toast went on claiming the move.
     */
    awaitingAcceptance: number;
    /**
     * Talents this ask moved from Acquisition to Vetting. A re-ask to someone
     * already in Vetting moves nothing — it is not counted here.
     */
    advanced: number;
    /**
     * D2 — asks that became a REQUEST the talent answers (Share / Not now /
     * Decline): nothing widens until they do.
     */
    requested: number;
    /** Asks the relationship already covered: the talent was reminded of what they owe. */
    reminded: number;
    /** Asks already answered in full — nothing was left to do, so nothing was sent. */
    alreadyShared: number;
  }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.TALENTS}/invite-to-vetting`, input);
  }

  /**
   * Update talent
   */
  async updateTalent(id: string, input: UpdateTalentInput): Promise<Talent> {
    return this.client.patch<Talent>(`${API_ENDPOINTS.BUSINESS.TALENTS}/${id}`, input);
  }

  /**
   * Update talent pipeline stage (with audit trail)
   */
  async updatePipelineStage(
    talentId: string,
    input: UpdatePipelineStageInput
  ): Promise<{ talent: Talent; transition: PipelineTransition }> {
    return this.client.patch<{ talent: Talent; transition: PipelineTransition }>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/pipeline-stage`,
      input
    );
  }

  /**
   * Soft-archive ONE journey. Used for Vetting / Sponsored cards where
   * hard-delete is unsafe (it would wipe checkup history, IRR events,
   * journey, pipeline transitions). The backend archives the journey
   * row and moves the card to the Archived tab; CheckUp results, IRR
   * events and pipeline transitions stay on file. Reversible via
   * `unarchiveJourney`.
   *
   * Scoped on the JOURNEY id, mirroring `unarchiveJourney`. A talent
   * can hold several live journeys in one tenant (re-invitation,
   * multi-opportunity); the talent-scoped route could not tell them
   * apart and resolved whichever row the database returned first.
   */
  async archiveJourney(
    journeyId: string,
    body: { reason?: string } = {}
  ): Promise<{ success: boolean; talentId: string; journeyId: string; message: string }> {
    return this.client.post<{
      success: boolean;
      talentId: string;
      journeyId: string;
      message: string;
    }>(API_ENDPOINTS.BUSINESS.JOURNEY_ARCHIVE(journeyId), body);
  }

  /**
   * Restore (un-archive) a previously archived journey. Same-row
   * reactivation: the journey id passed in is the same id that comes
   * back live. All hero data (CheckUp, iQulture, IRR events,
   * pipeline_transitions) remains attached.
   *
   * Returns the journey id + kanban stage the card landed in (its
   * pre-archive stage), so the caller can route the user there on
   * success.
   */
  async unarchiveJourney(journeyId: string): Promise<{
    success: boolean;
    talentId: string;
    journeyId: string;
    restoredToStage: string;
    message: string;
  }> {
    return this.client.post<{
      success: boolean;
      talentId: string;
      journeyId: string;
      restoredToStage: string;
      message: string;
    }>(API_ENDPOINTS.BUSINESS.JOURNEY_UNARCHIVE(journeyId));
  }

  // Program Methods
  /**
   * Get programs.
   *
   * `view` mirrors the Pipeline contract:
   *   • `'active'`   (default) — excludes archived
   *   • `'archived'` — only archived (for the Archived scope tab)
   *   • `'all'`      — both
   *
   * Omitting `view` preserves pre-2026-Q3 behavior (active only).
   */
  async getPrograms(opts: { view?: 'active' | 'archived' | 'all' } = {}): Promise<Program[]> {
    return this.client.get<Program[]>(API_ENDPOINTS.BUSINESS.PROGRAMS, {
      params: opts.view ? { view: opts.view } : undefined,
    });
  }

  /**
   * Get program by ID
   */
  async getProgram(id: string): Promise<Program> {
    return this.client.get<Program>(`${API_ENDPOINTS.BUSINESS.PROGRAMS}/${id}`);
  }

  /**
   * Create program
   */
  async createProgram(input: CreateProgramInput): Promise<Program> {
    return this.client.post<Program>(API_ENDPOINTS.BUSINESS.PROGRAMS, input);
  }

  /**
   * Update program. Accepts the full UpdateProgramInput shape including
   * `status` (used by the Programs list pause/resume action).
   */
  async updateProgram(id: string, input: UpdateProgramInput): Promise<Program> {
    return this.client.patch<Program>(`${API_ENDPOINTS.BUSINESS.PROGRAMS}/${id}`, input);
  }

  /**
   * Archive a program. Backend performs a soft-delete by setting
   * `status='archived'` AND cascade-archives every active cohort under
   * the program (2026-Q3). Response carries the cascade count so the
   * UI can show "Program archived. N cohort(s) cascade-archived."
   */
  async deleteProgram(id: string): Promise<{
    success: boolean;
    programId: string;
    cascadeArchivedCohortIds: string[];
    cascadeArchivedCohortCount: number;
    message: string;
  }> {
    return this.client.delete(`${API_ENDPOINTS.BUSINESS.PROGRAMS}/${id}`);
  }

  /**
   * Restore an archived program (sets `status='active'`). Idempotent.
   *
   * Intentionally does NOT cascade-restore cohorts — admins triage
   * cohort by cohort from the Cohorts Archived tab. Response includes
   * `archivedCohortCount` so the UI can hint "N cohort(s) still
   * archived — restore individually if needed."
   */
  async restoreProgram(id: string): Promise<{
    success: boolean;
    programId: string;
    status: string;
    archivedCohortCount: number;
    message: string;
  }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.PROGRAMS}/${id}/restore`, {});
  }

  // Cohort Methods
  /**
   * Get cohorts.
   *
   * Filters:
   *   • `programId` — optional program scope (server-side filter)
   *   • `view`      — scope tab selector; see `getPrograms` doc
   */
  async getCohorts(
    opts: { programId?: string; view?: 'active' | 'archived' | 'all' } = {}
  ): Promise<Cohort[]> {
    const params: Record<string, string> = {};
    if (opts.programId) params['programId'] = opts.programId;
    if (opts.view) params['view'] = opts.view;
    return this.client.get<Cohort[]>(API_ENDPOINTS.BUSINESS.COHORTS, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
  }

  /**
   * Get cohort by ID. Returns the enriched detail shape (includes
   * programName, talentCount, progress, avgReadiness, stageCounts).
   */
  async getCohort(id: string): Promise<CohortDetail> {
    return this.client.get<CohortDetail>(`${API_ENDPOINTS.BUSINESS.COHORTS}/${id}`);
  }

  /**
   * Create cohort. `programId` is optional — cohorts can exist
   * standalone since migration 0041.
   */
  async createCohort(input: CreateCohortInput): Promise<Cohort> {
    return this.client.post<Cohort>(API_ENDPOINTS.BUSINESS.COHORTS, input);
  }

  /**
   * Update cohort. Accepts the full UpdateCohortInput shape including
   * `status` (paused/resumed), `feedbackCadence`, etc.
   */
  async updateCohort(id: string, input: UpdateCohortInput): Promise<Cohort> {
    return this.client.patch<Cohort>(`${API_ENDPOINTS.BUSINESS.COHORTS}/${id}`, input);
  }

  /**
   * Persist a Program-level Care-strategy override (Enterprise + admin only).
   * Stored under `programs.settings.care`; feeds the cascade resolver.
   */
  async updateProgramCareStrategy(
    id: string,
    careConfig: Record<string, unknown>
  ): Promise<{ id: string; careConfig: Record<string, unknown> }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.PROGRAMS}/${id}/care-strategy`, {
      careConfig,
    });
  }

  /** Persist a Cohort-level Care-strategy override (Enterprise + admin only). */
  async updateCohortCareStrategy(
    id: string,
    careConfig: Record<string, unknown>
  ): Promise<{ id: string; careConfig: Record<string, unknown> }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.COHORTS}/${id}/care-strategy`, {
      careConfig,
    });
  }

  /**
   * The resolved EFFECTIVE Care strategy for a config scope (Country ← Company ←
   * Program ← Cohort). Powers the inherited/effective config preview on the
   * Program + Cohort detail pages — the real cascade, never a placeholder.
   */
  async getEffectiveCareStrategy(scope?: {
    programId?: string;
    cohortId?: string;
  }): Promise<EffectiveCareStrategy> {
    const params = new URLSearchParams();
    if (scope?.programId) params.set('programId', scope.programId);
    if (scope?.cohortId) params.set('cohortId', scope.cohortId);
    const qs = params.toString();
    return this.client.get(
      `${API_ENDPOINTS.BUSINESS.CARE}/effective-strategy${qs ? `?${qs}` : ''}`
    );
  }

  /**
   * Delete (archive) cohort. Backend performs a soft-delete by setting
   * `status='archived'`. The row remains queryable via `view: 'archived'`
   * and can be restored via `restoreCohort`.
   */
  async deleteCohort(id: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`${API_ENDPOINTS.BUSINESS.COHORTS}/${id}`);
  }

  /**
   * Restore an archived cohort (sets `status='active'`).
   * Idempotent — calling on an active cohort is a no-op success.
   */
  async restoreCohort(id: string): Promise<{ success: boolean; cohortId: string; status: string }> {
    return this.client.post<{ success: boolean; cohortId: string; status: string }>(
      `${API_ENDPOINTS.BUSINESS.COHORTS}/${id}/restore`,
      {}
    );
  }

  /**
   * Assign talents to cohort
   */
  /**
   * Assign talents to a cohort.
   *
   * `skipped` carries the supplied ids the server could not reach — an id that
   * is not an assignable talent of this tenant. It is returned rather than
   * swallowed because this call used to answer `{ added: 0 }` for a complete
   * no-op, which is how a caller sending the wrong kind of id went unnoticed.
   * A request where NOTHING matches is now a 400.
   */
  async assignTalentsToCohort(
    cohortId: string,
    talentIds: string[],
    /** The relationships being assigned — the selected cards; needed for a talent with several. */
    journeyIds?: string[]
  ): Promise<{ added: number; skipped: string[] }> {
    return this.client.post<{ added: number; skipped: string[] }>(
      `${API_ENDPOINTS.BUSINESS.COHORTS}/${cohortId}/talents`,
      { talentIds, ...(journeyIds ? { journeyIds } : {}) }
    );
  }

  // Analytics Methods
  /**
   * Get analytics summary
   */
  async getAnalyticsSummary(filters?: AnalyticsFilters): Promise<AnalyticsSummary> {
    return this.client.get<AnalyticsSummary>(API_ENDPOINTS.BUSINESS.ANALYTICS, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Get engagement analytics
   */
  async getEngagementAnalytics(
    filters?: AnalyticsFilters & { period?: 'day' | 'week' | 'month' }
  ): Promise<AnalyticsTimeSeries> {
    return this.client.get<AnalyticsTimeSeries>(`${API_ENDPOINTS.BUSINESS.ANALYTICS}/engagement`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Get MESH analytics
   */
  async getMeshAnalytics(
    filters?: AnalyticsFilters & { period?: 'day' | 'week' | 'month' }
  ): Promise<MeshAnalyticsSummary> {
    return this.client.get<MeshAnalyticsSummary>(`${API_ENDPOINTS.BUSINESS.ANALYTICS}/mesh`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  // Pipeline Methods
  /**
   * Get pipeline stats (counts per stage)
   */
  async getPipelineStats(filters?: {
    programId?: string;
    cohortId?: string;
  }): Promise<PipelineStats> {
    return this.client.get<PipelineStats>(`${API_ENDPOINTS.BUSINESS.ANALYTICS}/pipeline`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: VETTING
  // ═══════════════════════════════════════════════════════════════════════════

  /** One page of the Vetting list — searched, filtered and paged on the server. */
  async getVettingTalents(params: VettingTalentsParams): Promise<VettingTalentsResponse> {
    return this.client.get<VettingTalentsResponse>(`${API_ENDPOINTS.BUSINESS.VETTING}/talents`, {
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /** The Pipeline board's KPI row, counted by the server (D4). */
  async getPipelineSummary(params: PipelineSummaryParams): Promise<PipelineSummaryResponse> {
    return this.client.get<PipelineSummaryResponse>(API_ENDPOINTS.BUSINESS.PIPELINE_SUMMARY, {
      params: {
        view: params.view,
        programId: params.programId,
        cohortId: params.cohortId,
        q: params.q || undefined,
        status: params.status?.length ? params.status.join(',') : undefined,
        tag: params.tag?.length ? params.tag.join(',') : undefined,
      },
    });
  }

  async getVettingSummary(filters?: {
    programId?: string;
    cohortId?: string;
  }): Promise<VettingSummaryResponse> {
    return this.client.get<VettingSummaryResponse>(`${API_ENDPOINTS.BUSINESS.VETTING}/summary`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async getVettingOverview(filters?: {
    programId?: string;
    cohortId?: string;
  }): Promise<VettingOverview> {
    return this.client.get<VettingOverview>(`${API_ENDPOINTS.BUSINESS.VETTING}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async sponsorTalent(
    talentId: string,
    input: {
      /** The journey of the relationship — required when the talent has several with this company. */
      journeyId?: string;
      /** Sponsor on a journey of this company's own (D-D) — when the relationship's is shared. */
      dedicatedJourney?: boolean;
      programId?: string;
      cohortId?: string;
      branchName?: string;
      workLocation?: string;
      pricingPlan?: 'free' | 'enterprise';
      durationMonths?: number;
    }
  ): Promise<SponsorResult> {
    return this.client.post<SponsorResult>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/sponsor`,
      input
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: MOBILITY
  // ═══════════════════════════════════════════════════════════════════════════

  /** One page of the Mobility list — filtered and paged on the server. */
  async getMobilityTalents(params: MobilityTalentsParams): Promise<MobilityTalentsResponse> {
    return this.client.get<MobilityTalentsResponse>(`${API_ENDPOINTS.BUSINESS.MOBILITY}/talents`, {
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  async getMobilityOverview(filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }): Promise<MobilityOverview> {
    return this.client.get<MobilityOverview>(`${API_ENDPOINTS.BUSINESS.MOBILITY}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async getCompanyTasks(filters?: {
    talentId?: string;
    trackType?: string;
    status?: string;
  }): Promise<CompanyTask[]> {
    return this.client.get<CompanyTask[]>(`${API_ENDPOINTS.BUSINESS.MOBILITY}/tasks`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async updateCompanyTask(id: string, input: UpdateCompanyTaskInput): Promise<CompanyTask> {
    return this.client.patch<CompanyTask>(`${API_ENDPOINTS.BUSINESS.MOBILITY}/tasks/${id}`, input);
  }

  async uploadCompanyDocument(input: UploadCompanyDocumentInput): Promise<CompanyDocument> {
    return this.client.post<CompanyDocument>(`${API_ENDPOINTS.BUSINESS.MOBILITY}/documents`, input);
  }

  /**
   * R1 final push · F1-SDK — request a presigned S3 PUT URL for a
   * Mobility document upload. Returns `{ uploadUrl, key, expiresAt }`;
   * the FE then PUTs the file bytes directly to S3 and calls
   * `uploadCompanyDocument({ fileUrl: key, ... })` to persist the
   * metadata row.
   */
  async getMobilityDocumentUploadUrl(input: {
    /** The kit document this upload fills — it names the journey. */
    documentId: string;
    trackType: string;
    fileName: string;
    fileType: string;
    fileSize: number;
  }): Promise<{ uploadUrl: string; key: string; expiresAt: string }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.MOBILITY}/documents/upload-url`, input);
  }

  /**
   * R1 final push · F2-SDK — fire a Bell reminder for a Company
   * Action. Backend rate-limits to 1 reminder per task per 6h; if
   * the limit is hit, the response is a 429 with `{ code:
   * 'REMINDER_TOO_SOON', nextAllowedAt }` which the FE renders as a
   * friendly "Recently reminded" toast.
   */
  async remindCompanyTask(taskId: string): Promise<{ reminded: boolean; sendId: string | null }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.MOBILITY}/tasks/${taskId}/remind`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: ADAPTATION
  // ═══════════════════════════════════════════════════════════════════════════

  /** One page of the Adaptation list — graded, filtered and paged on the server. */
  async getAdaptationTalents(params: AdaptationTalentsParams): Promise<AdaptationTalentsResponse> {
    return this.client.get<AdaptationTalentsResponse>(
      `${API_ENDPOINTS.BUSINESS.ADAPTATION}/talents`,
      { params: params as unknown as Record<string, string | number | boolean | undefined> }
    );
  }

  /** Coach one talent, a group or a band — the server chooses the audience and each action. */
  async coachAdaptation(request: AdaptationCoachingRequest): Promise<AdaptationCoachingResult> {
    return this.client.post<AdaptationCoachingResult>(
      `${API_ENDPOINTS.BUSINESS.ADAPTATION}/coaching`,
      request
    );
  }

  async getAdaptationOverview(filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }): Promise<AdaptationOverview> {
    return this.client.get<AdaptationOverview>(`${API_ENDPOINTS.BUSINESS.ADAPTATION}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: CARE
  // ═══════════════════════════════════════════════════════════════════════════

  /** One page of the Care list — graded, filtered and paged on the server. */
  async getCareTalents(params: CareTalentsParams): Promise<CareTalentsResponse> {
    return this.client.get<CareTalentsResponse>(`${API_ENDPOINTS.BUSINESS.CARE}/talents`, {
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /** One Care plan for a selection — the ticked engagements or every one matching the filters. */
  async createCareInterventionBatch(
    input: CareInterventionBatchInput
  ): Promise<CareInterventionBatchResult> {
    return this.client.post<CareInterventionBatchResult>(
      `${API_ENDPOINTS.BUSINESS.CARE}/interventions/batch`,
      input
    );
  }

  async getCareOverview(filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }): Promise<CareOverview> {
    return this.client.get<CareOverview>(`${API_ENDPOINTS.BUSINESS.CARE}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: EVIDENCE
  // ═══════════════════════════════════════════════════════════════════════════

  /** One page of the Evidence passports — filtered and paged on the server. */
  async getEvidenceTalents(params: EvidenceTalentsParams): Promise<EvidenceTalentsResponse> {
    return this.client.get<EvidenceTalentsResponse>(`${API_ENDPOINTS.BUSINESS.EVIDENCE}/talents`, {
      params: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  async getEvidenceOverview(filters?: {
    programId?: string;
    cohortId?: string;
  }): Promise<EvidenceOverview> {
    return this.client.get<EvidenceOverview>(`${API_ENDPOINTS.BUSINESS.EVIDENCE}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 4: POOL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Pool overview for the calling tenant — the whole-pool summary stats
   * (`total`, assessed-mean `avgReadiness`) plus a rich-first candidate sample
   * for the page's facet/role fallbacks. Frontend reads `location` and
   * `originCountry` directly (no field renames in the consumer).
   */
  async getPoolOverview(filters?: {
    programId?: string;
    limit?: number;
  }): Promise<PoolOverviewResponse> {
    return this.client.get<PoolOverviewResponse>(`${API_ENDPOINTS.BUSINESS.POOL}/overview`, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async searchPool(params: PoolSearchParams): Promise<PoolListResponse> {
    // The `pool/search` endpoint takes `skills` + `locations` as single
    // comma-separated query params; flatten the arrays to match them on the wire.
    // `roles` travels as a JSON array: a job title may itself contain a comma.
    const { skills, locations, iqultureBadges, roles, ...rest } = params;
    const wire: Record<string, string | number | boolean | undefined> = {
      ...(rest as unknown as Record<string, string | number | boolean | undefined>),
      ...(skills?.length ? { skills: skills.join(',') } : {}),
      ...(locations?.length ? { locations: locations.join(',') } : {}),
      ...(iqultureBadges?.length ? { iqultureBadges: iqultureBadges.join(',') } : {}),
      ...(roles?.length ? { roles: JSON.stringify(roles) } : {}),
    };
    return this.client.get<PoolListResponse>(`${API_ENDPOINTS.BUSINESS.POOL}/search`, {
      params: wire,
    });
  }

  /**
   * Smart (Job-Description) pool search (BL-207). POSTs the JD; the backend
   * extracts + embeds it, ranks the pool, and returns candidates with a
   * `matchScore` + structured `matchReasons`, plus the extracted criteria.
   */
  async smartSearchPool(input: PoolSmartSearchRequest): Promise<PoolSmartSearchResponse> {
    return this.client.post<PoolSmartSearchResponse>(
      `${API_ENDPOINTS.BUSINESS.POOL}/smart-search`,
      input
    );
  }

  /** Distinct skill + location facets across the whole searchable pool. */
  /**
   * Type-ahead facet options (BL-207) — the Search-tab dropdowns query this as
   * the user types instead of loading every value (the pool holds ~12k skills /
   * ~900 roles). Ranked by frequency; an empty `q` returns the top defaults.
   */
  async searchPoolFacet(field: PoolFacetField, q: string, limit = 20): Promise<string[]> {
    return this.client.get<string[]>(`${API_ENDPOINTS.BUSINESS.POOL}/facet-options`, {
      params: { field, q: q || undefined, limit },
    });
  }

  /** Saved pool searches (BL-207) — tenant-scoped named JD + skill filters. */
  async listSavedPoolSearches(): Promise<PoolSavedSearchDto[]> {
    return this.client.get<PoolSavedSearchDto[]>(`${API_ENDPOINTS.BUSINESS.POOL}/saved-searches`);
  }

  async createSavedPoolSearch(input: PoolSavedSearchCreate): Promise<PoolSavedSearchDto> {
    return this.client.post<PoolSavedSearchDto>(
      `${API_ENDPOINTS.BUSINESS.POOL}/saved-searches`,
      input
    );
  }

  async deleteSavedPoolSearch(id: string): Promise<{ id: string }> {
    return this.client.delete<{ id: string }>(
      `${API_ENDPOINTS.BUSINESS.POOL}/saved-searches/${id}`
    );
  }

  async getPoolTalent(id: string): Promise<PoolTalentDetail> {
    return this.client.get<PoolTalentDetail>(`${API_ENDPOINTS.BUSINESS.POOL}/talents/${id}`);
  }

  /** Short-lived presigned URL for a pool candidate's CV (BL-207). */
  async getPoolTalentCvUrl(id: string): Promise<{ url: string; fileName: string }> {
    return this.client.get<{ url: string; fileName: string }>(
      `${API_ENDPOINTS.BUSINESS.POOL}/talents/${id}/cv`
    );
  }

  async addPoolTalentToPipeline(
    id: string,
    input: { cohortId: string }
  ): Promise<{ talentId: string; invitationId: string }> {
    // Creates a DRAFT invitation in Acquisition (invitation-first model, BL-207).
    return this.client.post<{ talentId: string; invitationId: string }>(
      `${API_ENDPOINTS.BUSINESS.POOL}/talents/${id}/add`,
      input
    );
  }

  /**
   * Bulk add several pool talents to the Acquisition stage under one cohort
   * (BL-207). Returns per-talent outcomes so the UI can report partial success.
   */
  async addPoolTalentsBulk(input: {
    talentIds: string[];
    /** Every invitation belongs to a cohort; the program follows from it. */
    cohortId: string;
  }): Promise<PoolBulkAddResult> {
    return this.client.post<PoolBulkAddResult>(
      `${API_ENDPOINTS.BUSINESS.POOL}/talents/add-bulk`,
      input
    );
  }

  /**
   * Tonnie-internal full-profile Pool import (BL-207). Creates real talents +
   * opts them into the pool. Restricted to the Tonnie internal tenant (403
   * otherwise). Returns per-batch counts.
   */
  async importPoolTalents(input: {
    talents: Array<{
      email: string;
      firstName?: string;
      lastName?: string;
      jobTitle?: string;
      currentCountry?: string;
      currentCity?: string;
      skills?: string[];
    }>;
  }): Promise<PoolImportResult> {
    return this.client.post<PoolImportResult>(`${API_ENDPOINTS.BUSINESS.POOL}/import`, input);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AFFILIATE PROGRAM (BL-213)
  // ═══════════════════════════════════════════════════════════════════════════

  async getAffiliateStatus(): Promise<{ enrolled: boolean }> {
    return this.client.get<{ enrolled: boolean }>(API_ENDPOINTS.BUSINESS.AFFILIATE_STATUS);
  }

  async getAffiliateOverview(params?: AffiliateOverviewParams): Promise<AffiliateOverview> {
    return this.client.get<AffiliateOverview>(API_ENDPOINTS.BUSINESS.AFFILIATE_OVERVIEW, {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /** Tonnie-wide cross-tenant overview (B10). 403 unless the caller is Tonnie staff. */
  async getAffiliatePlatformOverview(): Promise<AffiliatePlatformOverview> {
    return this.client.get<AffiliatePlatformOverview>(
      API_ENDPOINTS.BUSINESS.AFFILIATE_PLATFORM_OVERVIEW
    );
  }

  async getAffiliateRedemptions(
    params?: AffiliateRedemptionParams
  ): Promise<AffiliateRedemptionList> {
    return this.client.get<AffiliateRedemptionList>(API_ENDPOINTS.BUSINESS.AFFILIATE_REDEMPTIONS, {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /** Download the FILTERED redemptions as a CSV blob (whole dataset). */
  async getAffiliateRedemptionsCsv(params?: AffiliateExportParams): Promise<Blob> {
    return this.client.get(API_ENDPOINTS.BUSINESS.AFFILIATE_REDEMPTIONS_CSV, {
      params: params as Record<string, string | number | boolean | undefined>,
      responseType: 'blob',
    });
  }

  async getAffiliatePayouts(): Promise<AffiliatePayout[]> {
    return this.client.get<AffiliatePayout[]>(API_ENDPOINTS.BUSINESS.AFFILIATE_PAYOUTS);
  }

  async requestAffiliatePayout(): Promise<{ id: string; amount: number; status: string }> {
    return this.client.post<{ id: string; amount: number; status: string }>(
      API_ENDPOINTS.BUSINESS.AFFILIATE_PAYOUTS
    );
  }

  async cancelAffiliatePayout(payoutId: string): Promise<{ id: string; status: string }> {
    return this.client.post<{ id: string; status: string }>(
      API_ENDPOINTS.BUSINESS.AFFILIATE_PAYOUT_CANCEL(payoutId)
    );
  }

  async createAffiliateInvoiceUploadUrl(
    payoutId: string,
    file: { fileName: string; contentType: string }
  ): Promise<{ uploadUrl: string; fileKey: string }> {
    return this.client.post<{ uploadUrl: string; fileKey: string }>(
      API_ENDPOINTS.BUSINESS.AFFILIATE_PAYOUT_INVOICE_URL(payoutId),
      file
    );
  }

  async attachAffiliateInvoice(
    payoutId: string,
    fileKey: string
  ): Promise<{ id: string; status: string }> {
    return this.client.post<{ id: string; status: string }>(
      API_ENDPOINTS.BUSINESS.AFFILIATE_PAYOUT_INVOICE(payoutId),
      { fileKey }
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 4: DASHBOARD & ACTIVITY
  // ═══════════════════════════════════════════════════════════════════════════

  async getDashboard(filters?: { programId?: string }): Promise<BusinessDashboard> {
    return this.client.get<BusinessDashboard>(API_ENDPOINTS.BUSINESS.DASHBOARD, {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async getActivity(params?: { limit?: number; since?: string }): Promise<ActivityFeed> {
    return this.client.get<ActivityFeed>(API_ENDPOINTS.BUSINESS.ACTIVITY, {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 5: TEAM & SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════

  async inviteTeamMember(data: {
    email: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    department?: string;
  }): Promise<{ id: string; email: string; status: string }> {
    return this.client.post<{ id: string; email: string; status: string }>(
      '/api/business/team/invite',
      data
    );
  }

  async removeTeamMember(userId: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`/api/business/team/${userId}`);
  }

  async listTeamInvitations(): Promise<TeamInvitation[]> {
    return this.client.get<TeamInvitation[]>('/api/business/team/invitations');
  }

  async resendTeamInvitation(
    invitationId: string
  ): Promise<{ success: boolean; expiresAt: string }> {
    return this.client.post<{ success: boolean; expiresAt: string }>(
      `/api/business/team/invitations/${invitationId}/resend`
    );
  }

  async revokeTeamInvitation(invitationId: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(
      `/api/business/team/invitations/${invitationId}`
    );
  }

  /**
   * Public: who invited this link and to what — read before the invitee signs
   * in. A refusal carries a `TEAM_INVITATION_*` code (never read the message).
   */
  async getTeamInvitationInfo(token: string): Promise<InvitationInfo> {
    return this.client.get<InvitationInfo>(
      `/api/business/team/invitations/${encodeURIComponent(token)}/info`
    );
  }

  async acceptTeamInvitation(token: string): Promise<AcceptInvitationResult> {
    return this.client.post<AcceptInvitationResult>(
      `/api/business/team/invitations/${token}/accept`
    );
  }

  async getTenantSettings(): Promise<TenantSettings> {
    return this.client.get<TenantSettings>('/api/business/tenant/settings');
  }

  async updateTenantSettings(data: Partial<TenantSettings>): Promise<TenantSettings> {
    return this.client.patch<TenantSettings>('/api/business/tenant/settings', data);
  }

  /**
   * Delete the caller's tenant (organization). Admin-only on the server;
   * body must carry the exact tenant name for double-confirmation.
   * On success the caller should sign the user out — all memberships are
   * revoked and the Clerk `publicMetadata.tenantId` has been cleared.
   */
  async deleteTenant(confirmationName: string): Promise<{
    success: true;
    affectedUserIds: string[];
    subscriptionsCancelled: number;
    subscriptionsFailed: number;
  }> {
    return this.client.delete<{
      success: true;
      affectedUserIds: string[];
      subscriptionsCancelled: number;
      subscriptionsFailed: number;
    }>('/api/business/tenant', { confirmationName });
  }

  /**
   * Resolve a company domain into a structured profile (logo, name,
   * industry, size, HQ, LinkedIn, founded year, ...) via the
   * onboarding enrichment endpoint. Backed by Brandfetch, the BRAIN
   * LLM extractor, and heuristics — all orchestrated server-side.
   *
   * Pass `{ force: true }` to bypass the 90-day cache and re-run the
   * full provider chain. Used by the wizard's "Retry auto-detect"
   * button to recover from previously-cached empty results.
   */
  async enrichDomain(domain: string, options?: { force?: boolean }): Promise<EnrichResponse> {
    return this.client.post<EnrichResponse>('/api/business/onboarding/enrich', {
      domain,
      ...(options?.force ? { force: true } : {}),
    });
  }

  async getAuditLogs(filters?: {
    /** A registered code — the API refuses any other. */
    action?: AuditAction;
    userId?: string;
    /** ISO date string — inclusive lower bound on `createdAt`. */
    startDate?: string;
    /** ISO date string — inclusive upper bound on `createdAt`. */
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: AuditLogEntry[]; total: number }> {
    return this.client.get<{ logs: AuditLogEntry[]; total: number }>('/api/business/audit-logs', {
      params: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  async getMeshScores(talentIds: string[]): Promise<
    Record<
      string,
      {
        /** `null` without the weighted iQulture score. */
        overall: number | null;
        /** Each pillar is `null` when it was never scored — never 0. */
        motivation: number | null;
        english: number | null;
        softSkills: number | null;
        hardSkills: number | null;
      } | null
    >
  > {
    return this.client.get('/api/business/talents/mesh-scores', {
      params: { talentIds: talentIds.join(',') },
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MESSAGES
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // Messaging lives in `MessagesService` (see `./messages.service.ts`)
  // and is consumed via the `useMessage*` hooks in
  // `./hooks/useMessages.ts`. The previous BusinessService.* mirrors
  // (getMessageTemplates / createMessageTemplate / updateMessageTemplate /
  // deleteMessageTemplate / activateMessageTemplate / sendMessage /
  // getMessageAnalytics) were unused duplicates with stale shapes — the
  // dead `sendMessage` here was even pointing at older request/response
  // types that no longer matched the backend contract. Removed in favor
  // of the single canonical messaging surface.

  // ═══════════════════════════════════════════════════════════════════════════
  // TONNIE SERVICE REQUESTS — "Ask Tonnie Services" modal
  // ═══════════════════════════════════════════════════════════════════════════

  async listTonnieServiceRequests(filters?: {
    status?: TonnieServiceRequestStatus;
    limit?: number;
    offset?: number;
  }): Promise<{ requests: TonnieServiceRequest[] }> {
    return this.client.get<{ requests: TonnieServiceRequest[] }>(
      '/api/business/services/requests',
      {
        params: filters as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  async createTonnieServiceRequest(input: CreateTonnieServiceRequestInput): Promise<{
    id: string;
    status: TonnieServiceRequestStatus;
    submittedAt: string | null;
  }> {
    return this.client.post('/api/business/services/requests', input);
  }

  async cancelTonnieServiceRequest(
    id: string
  ): Promise<{ id: string; status: TonnieServiceRequestStatus }> {
    return this.client.patch(`/api/business/services/requests/${id}/cancel`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SPONSORSHIP PRICING (replaces hardcoded constant in @tonnie/foundation)
  // ═══════════════════════════════════════════════════════════════════════════

  async getSponsorshipPricing(): Promise<SponsorshipPricing> {
    return this.client.get<SponsorshipPricing>('/api/billing/sponsorship-pricing');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TALENT DETAIL — admin per-talent surfaces
  // ═══════════════════════════════════════════════════════════════════════════

  async getTalentActivity(
    talentId: string,
    options?: { journeyId?: string; limit?: number; cursor?: string }
  ): Promise<TalentActivityFeed> {
    return this.client.get<TalentActivityFeed>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/activity`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  async getTalentCheckup(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<TalentCheckupDetail> {
    return this.client.get<TalentCheckupDetail>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/checkup`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  /**
   * Full Brain CheckUp DTO for the admin Vetting drawer. Same shape
   * journey-web's `/checkup/results` page consumes — extended with
   * `pitchVideoAnalysis.playbackUrl` (presigned S3 URL resolved
   * server-side from the citizen's vault upload). Returns null when
   * the CheckUp hasn't been calculated yet.
   *
   * Tenant-scoped at the controller level — `talentId` must belong to
   * a journey owned by the caller's tenant or the endpoint 404s before
   * any data is read.
   *
   * W8.D — REJECTS with a 403 `TonnieApiError` whose `code` is
   * `CONSENT_SCOPE_WITHHELD` (and whose `data` carries `{ scope, journeyInstanceId }`)
   * when the citizen has revoked the `checkup` scope. It cannot answer `null`
   * for that case: `null` already means "not calculated yet" here, and one
   * value meaning two things is exactly how a withheld CheckUp gets rendered
   * as a talent who never took one. Branch on the code and render the withheld
   * treatment — never fall through to a generic error toast, and never let the
   * consuming surface zero-fill the missing scores.
   */
  async getTalentCheckupResults(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<CheckUpResults | null> {
    return this.client.get<CheckUpResults | null>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/checkup-results`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  /**
   * Admin "Re-run pitch analysis" / "Force recalculate" trigger.
   *
   * POSTs to the tenant-isolated business endpoint instead of the
   * citizen `/api/heroes/checkup/:id/calculate` route — admins don't
   * own the talent's `user_id` and the citizen endpoint enforces
   * `talent.user_id === ctx.userId`, which is exactly the check this
   * admin path is built to skip. Always force=true server-side; no
   * other knobs are exposed.
   *
   * Returns the same `CalculationTriggerResult` shape as the citizen
   * trigger so polling UI can be shared by the caller.
   */
  async recalculateTalentCheckup(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<CalculationTriggerResult> {
    const params = new URLSearchParams();
    if (options?.journeyId) params.set('journeyId', options.journeyId);
    const qs = params.toString();
    const url = qs
      ? `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/checkup/recalculate?${qs}`
      : `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/checkup/recalculate`;
    return this.client.post<CalculationTriggerResult>(url, {});
  }

  async getTalentMobility(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<TalentMobilityDetail> {
    return this.client.get<TalentMobilityDetail>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/mobility`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  async getTalentAdaptation(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<TalentAdaptationDetail> {
    return this.client.get<TalentAdaptationDetail>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/adaptation`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  async getTalentCare(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<TalentCareDetail> {
    return this.client.get<TalentCareDetail>(`${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/care`, {
      params: options as Record<string, string | number | boolean | undefined>,
    });
  }

  async getTalentEvidence(
    talentId: string,
    options?: { journeyId?: string }
  ): Promise<TalentEvidenceDetail> {
    return this.client.get<TalentEvidenceDetail>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/evidence`,
      {
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
  }

  async requestInterviewFirst(
    talentId: string,
    input: RequestInterviewFirstInput
  ): Promise<{ id: string; status: string; requestedAt: string | null }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/interview-first`, input);
  }

  // ─── Unified Care intervention lifecycle (R1) ───
  // The single contract for every Care intervention: one taxonomy + lifecycle
  // backed by `POST/PATCH /api/business/care/interventions`. Input shapes come
  // from `@tonnie/schemas` (`createCareInterventionSchema` /
  // `resolveCareInterventionSchema`) so FE forms + the API validate the same Zod.

  /** Create a unified Care intervention (one-shot or scheduled). */
  async createCareIntervention(
    input: CreateCareInterventionInput
  ): Promise<{ id: string; status: string }> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.CARE}/interventions`, input);
  }

  /** Edit the descriptive fields (type/title/description/priority/link) of an open interaction. */
  async updateCareIntervention(
    id: string,
    input: UpdateCareInterventionInput
  ): Promise<{ id: string; status: string }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}`, input);
  }

  /** Resolve an intervention with an outcome (+ optional resolution note). */
  async resolveCareIntervention(
    id: string,
    input: ResolveCareInterventionInput
  ): Promise<{ id: string; status: string }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/resolve`, input);
  }

  /** Cancel a scheduled/pending intervention (no outcome). */
  async cancelCareIntervention(id: string): Promise<{ id: string; status: string }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/cancel`, {});
  }

  /** Move a scheduled interaction to a new date/time. */
  async rescheduleCareIntervention(
    id: string,
    input: { scheduledAt: string }
  ): Promise<{ id: string; status: string }> {
    return this.client.patch(
      `${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/reschedule`,
      input
    );
  }

  /** Delegate (or clear) the owner of an interaction. */
  async reassignCareIntervention(
    id: string,
    input: { assignedTo: string | null }
  ): Promise<{ id: string; status: string }> {
    return this.client.patch(`${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/assign`, input);
  }

  /** The internal collaboration thread for an interaction. */
  async getCareInterventionComments(id: string): Promise<CareInterventionComment[]> {
    return this.client.get<CareInterventionComment[]>(
      `${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/comments`
    );
  }

  /** Append a comment to the interaction's internal thread. */
  async addCareInterventionComment(
    id: string,
    input: { content: string }
  ): Promise<CareInterventionComment> {
    return this.client.post(`${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/comments`, input);
  }

  /** Cross-talent interactions inbox — `open` (default), `done`, or `all`. */
  async getCareInterventionInbox(params: CareInboxParams): Promise<CareInterventionInbox> {
    const { mine, q, ...rest } = params;
    return this.client.get<CareInterventionInbox>(
      `${API_ENDPOINTS.BUSINESS.CARE}/interventions/inbox`,
      {
        params: {
          ...rest,
          ...(mine ? { mine: 'true' } : {}),
          ...(q?.trim() ? { q: q.trim() } : {}),
        },
      }
    );
  }

  /** Full detail for one interaction (for the Care-page detail drawer). */
  async getCareInterventionDetail(id: string): Promise<CareInterventionDetail> {
    return this.client.get<CareInterventionDetail>(
      `${API_ENDPOINTS.BUSINESS.CARE}/interventions/${id}/detail`
    );
  }

  // ─── Objectives-based feedback (BL-125) ───

  /** Talents (visible to this business) with an OPEN objective-feedback cycle. */
  async getObjectiveFeedbackDue(): Promise<ObjectiveFeedbackDueResponse> {
    return this.client.get<ObjectiveFeedbackDueResponse>(
      `${API_ENDPOINTS.BUSINESS.CARE}/objective-feedback/due`
    );
  }

  /** Business view of ONE journey's objectives + current cycle + feedback entries (X7). */
  async getTalentObjectives(talentId: string, journeyId: string): Promise<TalentObjectivesView> {
    return this.client.get<TalentObjectivesView>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/care/objectives`,
      { params: { journeyId } }
    );
  }

  /** Company-wide objectives achievement rollup. */
  async getObjectivesOverview(): Promise<CareObjectivesOverview> {
    return this.client.get<CareObjectivesOverview>(
      `${API_ENDPOINTS.BUSINESS.CARE}/objectives/overview`
    );
  }

  /** The calling provider (admin / team member) submits objective feedback. */
  async submitTalentObjectiveFeedback(
    talentId: string,
    journeyId: string,
    input: SubmitObjectiveFeedbackInput
  ): Promise<CareFeedbackCycle> {
    return this.client.post<CareFeedbackCycle>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/care/objective-feedback`,
      input,
      { params: { journeyId } }
    );
  }

  /** HR overrides one talent's baseline/target for an objective (hybrid rule). */
  async setTalentObjectiveTarget(
    talentId: string,
    journeyId: string,
    objectiveId: string,
    input: { baselineValue?: number | null; targetValue?: number | null }
  ): Promise<CareObjective> {
    return this.client.patch<CareObjective>(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/care/objectives/${objectiveId}`,
      input,
      { params: { journeyId } }
    );
  }

  /**
   * Download the talent's report as a Blob.
   *
   * This replaced `buildTalentReportUrl`, which returned a bare string for a
   * plain `<a href download>` and could not work in either dimension (H10):
   *
   *   • The string was ROOT-RELATIVE (`/api/business/…`) and never prefixed
   *     with the API origin, so in business-web — where the API is on a
   *     different origin (`VITE_API_URL`) — the anchor resolved against the
   *     SPA's own host and downloaded the SPA's `index.html`.
   *   • An anchor navigation sends no `Authorization` header, and every route
   *     on `BusinessController` is Clerk-Bearer gated. So even at the right
   *     origin it was a guaranteed 401, delivered as a navigation the page
   *     cannot observe — no error, no toast, no console entry.
   *
   * Going through the client fixes both: it resolves against the configured
   * `baseUrl` and attaches a fresh bearer token, exactly as
   * `downloadCohortReportCsv` already did for the cohort export.
   */
  async downloadTalentReport(
    talentId: string,
    format: 'pdf' | 'csv',
    options?: { journeyId?: string }
  ): Promise<Blob> {
    return this.client.get<Blob>(`${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/report.${format}`, {
      responseType: 'blob',
      ...(options?.journeyId ? { params: { journeyId: options.journeyId } } : {}),
    });
  }

  /**
   * Download one cohort's talents as a CSV blob (H20/BW-5's sibling, BW-1).
   *
   * Fetched through the authenticated client rather than exposed as a URL for a
   * plain `<a href>`, deliberately: this endpoint is Clerk-Bearer authenticated
   * like every other, and a bare anchor sends no Authorization header — it would
   * 401 and the operator would see a broken download with no explanation.
   *
   * `withFilename: true` (D7): the server appends `-partial` to the filename
   * (`Content-Disposition`) when the roster was truncated — the one channel
   * this signal was ever meant to reach the operator through. `fetch`
   * otherwise discards the header once the body is read as a Blob, so the
   * caller rebuilt its own filename and silently dropped the signal.
   */
  async downloadCohortReportCsv(cohortId: string): Promise<BlobWithFilename> {
    return this.client.get<BlobWithFilename>(API_ENDPOINTS.BUSINESS.COHORT_REPORT_CSV(cohortId), {
      responseType: 'blob',
      withFilename: true,
    });
  }

  /**
   * BL-090 — presigned URL so an admin can download/verify ONE of a talent's
   * mobility documents. Tenant-isolated server-side via the visible-journey rule.
   *
   * W8.D — REJECTS with a 403 `TonnieApiError` whose `code` is
   * `CONSENT_SCOPE_WITHHELD` (`data: { scope: 'teleportal', journeyInstanceId }`)
   * when the citizen has revoked the scope. A presigned URL has nowhere to
   * carry a flag, so the refusal IS the marker; surface it as "withheld", not
   * as the generic download-failed toast, or the operator retries a request
   * that is never going to succeed.
   */
  async getMobilityDocumentDownloadUrl(
    talentId: string,
    documentId: string,
    options?: { journeyId?: string }
  ): Promise<{ downloadUrl: string; fileName: string }> {
    return this.client.get(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/mobility/documents/${documentId}/download-url`,
      { params: options?.journeyId ? { journeyId: options.journeyId } : undefined }
    );
  }

  /**
   * BL-090 — download the selected mobility documents as a single ZIP blob.
   *
   * W8.D — same refusal as `getMobilityDocumentDownloadUrl`: a 403
   * `CONSENT_SCOPE_WITHHELD` rather than an empty archive, because a ZIP that
   * is quietly shorter reads as a citizen who uploaded less.
   */
  async downloadMobilityDocumentsZip(
    talentId: string,
    input: { documentIds: string[]; journeyId?: string }
  ): Promise<Blob> {
    return this.client.post(
      `${API_ENDPOINTS.BUSINESS.TALENTS}/${talentId}/mobility/documents/download-zip`,
      input,
      { responseType: 'blob' }
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 3 TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** The Vetting tiles, counted by the server over the whole vetting population. */
export interface VettingLensSummary {
  totalTalents: number;
  /** `null` when no shared score exists — draw "no value", never 0. */
  avgIQulture: number | null;
  completedCheckups: number;
  pendingCheckups: number;
  proBadges: number;
  potentialBadges: number;
  noobBadges: number;
  noBadges: number;
  readyToSponsor: number;
  /** CheckUp candidates ready to sponsor (PRO / POTENTIAL, CheckUp completed). */
  readyCandidates: number;
  testitTotal: number;
  testitComplete: number;
}

export interface VettingLensCohort {
  /** `null` for talents in no cohort. */
  cohortId: string | null;
  count: number;
  /** `null` when the cohort has no shared score. */
  avgIQulture: number | null;
  completed: number;
  proBadges: number;
  potentialBadges: number;
  progress: number;
}

export interface VettingLensView {
  summary: VettingLensSummary;
  cohorts: VettingLensCohort[];
  /** Scores that exist and are not shared with this company — in no result figure. */
  withheld: { checkup: number };
}

/** The Pipeline board's filters, as `GET /business/pipeline/summary` reads them. */
export interface PipelineSummaryParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  status?: ReadonlyArray<'on-track' | 'at-risk' | 'blocked' | 'completed'>;
  tag?: readonly PipelineSummaryTag[];
}

/**
 * GET /business/pipeline/summary — the board's KPI row over the whole
 * pipeline (D4). The average runs over the scores the company may read.
 */
export interface PipelineSummaryResponse {
  total: number;
  onTrack: number;
  atRisk: number;
  blocked: number;
  completed: number;
  /** `null` when no shared score exists — draw "no value", never 0. */
  avgIqulture: number | null;
  scoredShared: number;
  /** Scores that exist and are not shared — left out of the average. */
  notShared: number;
  truncated: boolean;
}

/**
 * GET /business/vetting/summary — the Vetting lens for Active and Archived,
 * over the whole roster (P2.1). `truncated`: the roster read hit its ceiling.
 */
/** A Mobility card's facts, on top of its talent-list row (the server's `mobilityListFacts`). */
export type MobilityListRow = PipelineRow & {
  trackId: MobilityTrackFilter;
  allTracksDone: boolean;
  tracks: Array<{ id: MobilityTrackFilter; status: string; progress: number }>;
  overallProgress: number;
  mobilityStatus: MobilityListStatusFilter;
  pendingCompanyTaskCount: number;
  waitingOnCompany: boolean;
};

/** One page of the Mobility list (`GET /business/mobility/talents`). */
export interface MobilityTalentsResponse {
  items: MobilityListRow[];
  total: number;
  page: number;
  limit: number;
  /** The sponsored talents who have not started Mobility (active view only). */
  notStarted: NotStartedList;
  truncated: boolean;
}

export interface MobilityTalentsParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  track?: MobilityTrackFilter;
  status?: MobilityListStatusFilter;
  page: number;
  limit: number;
}

/** An Evidence passport: the talent-list row with its passport facts and what it earned. */
export type EvidencePassportRow = PipelineRow & {
  /** Where the engagement sits on the journey bar. */
  stage: EvidenceStageFilter;
  isSponsored: boolean;
  /** The lifecycle's status, else its attention severity — `unknown` with neither. */
  passportStatus: 'on-track' | 'at-risk' | 'blocked' | 'completed' | 'unknown';
  /** Earned where the citizen shares their passport with this company. */
  journeyMiles: number;
  stampCount: number;
  badgeCount: number;
};

/** One page of the Evidence passports (`GET /business/evidence/talents`). */
export interface EvidenceTalentsResponse {
  items: EvidencePassportRow[];
  total: number;
  page: number;
  limit: number;
  truncated: boolean;
}

export interface EvidenceTalentsParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  stage?: EvidenceStageFilter;
  page: number;
  limit: number;
}

/** A Vetting list row: the talent-list row, with the status its card shows. */
export type VettingListRow = PipelineRow & { vettingStatus: VettingStatusFilter };

/** One page of the Vetting list (`GET /business/vetting/talents`). */
export interface VettingTalentsResponse {
  items: VettingListRow[];
  /** Every row that matches — not the page length. */
  total: number;
  page: number;
  limit: number;
  /** PRO and POTENTIAL with a completed CheckUp, best first — over the whole list. */
  topCandidates: VettingListRow[];
  /** The roster read hit its ceiling. */
  truncated: boolean;
}

export interface VettingTalentsParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  status?: VettingStatusFilter;
  badge?: VettingBadgeFilter;
  page: number;
  limit: number;
}

export interface VettingSummaryResponse {
  active: VettingLensView;
  archived: VettingLensView;
  truncated: boolean;
}

export interface VettingOverview {
  talents: Array<{
    id: string;
    email: string;
    displayName: string;
    pipelineStage: string;
    createdAt: string;
    meshScores: Record<string, number | null> | null;
    /** `false` → the talent withholds their CheckUp result ("not shared"). */
    checkupShared: boolean;
  }>;
  total: number;
  /** Talents left out of this read because they withhold it from this company. */
  withheld: { checkup: number };
}

export interface SponsorResult {
  journey: { id: string };
  talent: { id: string; pipelineStage: string };
}

export interface MobilityOverview {
  talentCount: number;
  /** Talents in Mobility who do not share TelePortal — left out of `summary`. */
  withheld: { teleportal: number };
  talents: Array<{ id: string; displayName: string; email: string }>;
  taskStats: Record<string, number>;
  docStats: Record<string, number>;
  /** The Mobility page's lifecycle summary, over the rows of the requested view. */
  summary: {
    totalInMobility: number;
    inProgress: number;
    completed: number;
    blocked: number;
    atRisk: number;
    onTrack: number;
    /** The mean of the tracks' average progress; `null` when no track has any. */
    averageProgress: number | null;
  };
  /**
   * Per track: the engagement split (sums to `engaged`), the average progress
   * (`null` with none engaged) and the kit's REAL milestones by `templateId`
   * (render `t(titleKey)` — the kit's own key), completed of total journeys.
   */
  tracks: Array<{
    id: 'legal' | 'travel' | 'setup';
    engaged: number;
    done: number;
    blocked: number;
    inProgress: number;
    notStarted: number;
    progress: number | null;
    milestones: Array<{ templateId: string; titleKey: string; completed: number; total: number }>;
  }>;
  /** The journeys `summary.blocked` counts — list exactly these. */
  blockedJourneyIds: string[];
  /** Sponsored talents of this view who have not started Mobility. */
  notStarted: number;
  /**
   * The company-action numbers, counted by the server over every company task
   * and document (the lists are capped for rendering — never count them).
   */
  companyActions: {
    pendingTasks: number;
    /** Open tasks, high priority or due within three days. */
    urgentTasks: number;
    overdueTasks: number;
    blockingDocs: number;
    byTrack: Record<'legal' | 'travel' | 'setup', { openTasks: number; pendingDocs: number }>;
    /** Pending company tasks per talent id (a talent with none is absent). */
    pendingByTalent: Record<string, number>;
    /** Talents with a pending high-priority company task. */
    blockedByCompany: string[];
    /** Every company task, counted by track and status — what a filtered badge sums. */
    tasksByTrackStatus: Array<{ track: string; status: string; count: number }>;
  };
  /** Blocked journeys whose talent a pending high-priority company task holds up. */
  waitingOnCompany: number;
  /** Both tab badges: the Mobility engagements of each view, over its whole roster. */
  viewCounts: { active: number; archived: number };
  /** The roster read hit its ceiling — the counts are over its first rows. */
  truncated: boolean;
}

export interface CompanyTask {
  id: string;
  tenantId: string;
  talentId: string;
  /** BL-097: backend-owned display name of the talent this task belongs to. */
  talentName?: string;
  journeyId?: string;
  trackType: string;
  title: string;
  description?: string;
  priority: TaskPriority;
  status: TeleportalTaskStatus;
  dueDate?: string;
  // ── L5 region: server-derived lateness (`isTaskOverdue`), never a client rule ──
  isOverdue: boolean;
  // ── end L5 region ──
  /** Open, and high priority or due within three days — the server's rule. */
  isUrgent: boolean;
  completedAt?: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * BL-105: the documents this company task requires (task-centric model —
   * a company task may need one or more documents attached/uploaded before it
   * is complete, mirroring the citizen task model). Empty when the task needs
   * no document (then it is completed directly).
   */
  documents: CompanyDocument[];
}

/**
 * PATCH /business/mobility/tasks/:id — the fields a company may change on one
 * of its own tasks, in the column's vocabularies (the API refuses anything
 * else). Company tasks come from the TelePortal kit; there is no create.
 */
export interface UpdateCompanyTaskInput {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  status?: TeleportalTaskStatus;
  assignedTo?: string;
}

export interface CompanyDocument {
  id: string;
  tenantId: string;
  talentId: string;
  /** BL-097: backend-owned display name of the talent this document belongs to. */
  talentName?: string;
  /** BL-105: the company task this document belongs to (task-centric nesting). */
  taskId?: string;
  trackType: string;
  name: string;
  description?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: number;
  /** BL-105: the linked Vault file id once uploaded (null/absent = awaiting upload). */
  vaultFileId?: string;
  status: string;
  /** Kit-task flags: whether the document is required and whether a pending one
   *  blocks the talent's mobility progress (drives the "Blocking Docs" KPI). */
  required?: boolean;
  blocksProgress?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UploadCompanyDocumentInput {
  /**
   * BL-097: the kit `teleportal_documents` row id this upload fills (a
   * "Document To Upload" placeholder). Required — the backend links the
   * registered Vault file onto this row and flips it to `uploaded`.
   */
  documentId: string;
  talentId: string;
  trackType: string;
  name: string;
  description?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: number;
}

/** Canonical Adaptation engagement bands (backend-computed, single source). */
export type AdaptationEngagementLevel = 'thriving' | 'active' | 'slowing' | 'at_risk' | 'inactive';
export type AdaptationAttentionReason = 'inactive' | 'declining' | 'not_begun' | 'streak_at_risk';

/**
 * One talent's QultureUp evolution + status as the admin Adaptation cohort page
 * renders it. Every figure is graded against the canonical QultureUp catalog /
 * progress (matches the citizen hero + the per-talent drawer by construction).
 */
export interface AdaptationTalentRow {
  /**
   * The ENGAGEMENT this row is about — the journey id. (W7.1)
   *
   * Nullable only for a row whose journey could not be resolved. The page
   * opens a drawer from these rows, and without it the drawer could be given
   * only the talent id — so a talent holding two journeys in one tenant opened
   * whichever one a `find()` reached first.
   */
  journeyInstanceId: string | null;
  id: string;
  displayName: string;
  email: string | null;
  cohortId: string | null;
  cohortName: string | null;
  /** BL-066: true once the talent has begun QultureUp (canonical predicate). */
  started: boolean;
  /** round(missionsCompleted / missionsTotal_catalog × 100). */
  overallProgress: number;
  missionsCompleted: number;
  missionsTotal: number;
  tracksCompleted: number;
  tracksTotal: number;
  worldsCompleted: number;
  worldsTotal: number;
  level: string | null;
  levelScore: number | null;
  miles: number;
  currentStreak: number;
  longestStreak: number;
  weeklyMissionsCompleted: number;
  lastActivityDate: string | null;
  /** Canonical engagement signal (was hardcoded in the page). */
  engagementLevel: AdaptationEngagementLevel;
  daysInactive: number | null;
  needsAttention: boolean;
  streakAtRisk: boolean;
  attentionReason: AdaptationAttentionReason | null;
}

/** Per-world cohort rollup for the Worlds tab. */
export interface AdaptationWorldStat {
  worldCode: string;
  name: string;
  iconUrl: string | null;
  enrolled: number;
  completed: number;
  inProgress: number;
  avgProgress: number;
  missionsTotal: number;
}

/**
 * An Adaptation list row: the talent-list row with its QultureUp progress and
 * engagement, graded by the server — one per ENGAGEMENT.
 */
export type AdaptationListRow = PipelineRow & {
  started: boolean;
  overallProgress: number;
  missionsCompleted: number;
  missionsTotal: number;
  tracksCompleted: number;
  tracksTotal: number;
  worldsCompleted: number;
  worldsTotal: number;
  level: string | null;
  levelScore: number | null;
  miles: number;
  currentStreak: number;
  longestStreak: number;
  weeklyMissionsCompleted: number;
  lastActivityDate: string | null;
  engagementLevel: AdaptationEngagementLevel;
  daysInactive: number | null;
  needsAttention: boolean;
  streakAtRisk: boolean;
  /** Why the row needs support — beside the talent list's own `attentionReason`. */
  adaptationAttentionReason: AdaptationAttentionReason | null; /** The coaching action that fits the talent — the Coach dialog opens on it. */
  coachingAction: 'encourage' | 'challenge' | 'checkin' | 'celebrate' | 'remind';
};

/** One page of the Adaptation list (`GET /business/adaptation/talents`). */
export interface AdaptationTalentsResponse {
  items: AdaptationListRow[];
  total: number;
  page: number;
  limit: number;
  /** The sponsored talents who have not begun QultureUp (Active view only). */
  notStarted: NotStartedList;
  truncated: boolean;
}

export interface AdaptationTalentsParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  engagement?: AdaptationEngagementFilter;
  page: number;
  limit: number;
}

export interface AdaptationOverview {
  /** BL-066: total SPONSORED roster (engagement is parallel). */
  talentCount: number;
  /** BL-066: count who have STARTED Adaptation (headline + "X of Y"). */
  startedCount?: number;
  /** The Archived tab's badge: every archived engagement at the Adaptation stage. */
  archivedCount: number;
  /** The cohort's Adaptation health (started talents), graded server-side; `null` with none. */
  health: { score: number; band: AdaptationHealthBand } | null;
  /** The KPI strip, the engagement distribution and the key metrics, over every started row. */
  summary: {
    total: number;
    bands: Record<AdaptationEngagementLevel, number>;
    needsAttention: number;
    streaksAtRisk: number;
    avgMiles: number;
    avgProgress: number;
    missionsThisWeek: number;
  };
  /** Who needs attention, most urgent first: the whole count, and the first few. */
  attention: { total: number; talents: AdaptationListRow[] };
  topPerformers: AdaptationListRow[];
  /** The Coaching Center: each non-empty band, most urgent first — its total and first talents. */
  bands: Array<{ level: AdaptationEngagementLevel; total: number; talents: AdaptationListRow[] }>;
  /** How many each quick coaching action would reach. */
  quickActions: { inactive: number; streak: number; celebrate: number; challenge: number };
  /** Every graded row of the live roster (the Business Companion's overview tool). */
  talents: AdaptationTalentRow[];
  worldStats: AdaptationWorldStat[];
  /** Talents left off this page because they withhold QultureUp. */
  withheld: { qultureup: number };
  /** The roster read hit its ceiling — the counts are over its first rows. */
  truncated: boolean;
}

export type CommitmentLevel = 'champion' | 'committed' | 'engaged' | 'at_risk' | 'flight_risk';

export interface CommitmentComponents {
  checkinConsistency: number;
  /** `null` when not yet measurable (<2 completed checkpoints / no deviation data). */
  pillarStability: number | null;
  engagementLevel: number;
  /** `null` until the first checkpoint completes. */
  integrationProgress: number | null;
  /** `null` when arrival date is unknown. */
  tenureStability: number | null;
  /** Responsiveness to business-initiated care gestures (answered/delivered). `null` until a gesture is delivered. */
  careResponsiveness: number | null;
}

export interface CommitmentRiskSignal {
  type: string;
  severity: 'warning' | 'critical';
  /** i18n key under `care.risk.*`; FE translates with `details` as values. */
  messageKey: string;
  details?: Record<string, unknown> | null;
}

/** Per-talent commitment summary surfaced to the company (no raw mood). */
export interface TalentCommitment {
  /** Real index (0-100), or `null` during cold start ("Getting started"). Never fabricated. */
  index: number | null;
  /** Canonical band, or `null` when `index` is null (insufficient signal). */
  level: CommitmentLevel | null;
  /**
   * `true` once the talent crosses the cold-start threshold (≥1 completed
   * checkpoint OR ≥5 distinct check-in days). When `false`, this talent is
   * "Getting started" and excluded from cohort averages + the risk queue.
   */
  hasSufficientSignal: boolean;
  /** `null` when `index` is null (insufficient signal). */
  riskLevel: 'low' | 'medium' | 'high' | null;
  trend: 'improving' | 'stable' | 'declining';
  components: CommitmentComponents;
  riskSignals: CommitmentRiskSignal[];
}

/**
 * A Care row as the Care lens sends it — one per ENGAGEMENT, graded for the
 * caller's risk visibility (BL-122): the talent list's identity, cohort,
 * iQulture and archive state, with the commitment, pillars and the company's
 * objective-feedback state.
 */
export interface CareListRow {
  id: string;
  journeyInstanceId: string | null;
  displayName: string;
  email: string | null;
  cohortId: string | null;
  iqultureScore: number | null;
  iqultureBadge: IQultureBadgeId | null;
  /** The score is PROVISIONAL — a lower bound, some evidence missing (Option B). Draw it beside the score. */
  iqultureProvisional: boolean;
  /** What the complete evidence could reach; `null` unless provisional. */
  iqultureScoreMax: number | null;
  currentStage: string;
  archivedAt: string | null;
  preArchiveStage: string | null;
  lastActivity: string | null;
  /** BL-066: true once the talent has begun Care (canonical predicate). */
  started: boolean;
  /** Null until the talent has signals to score (then the real index — never a fake 50). */
  commitment: TalentCommitment | null;
  /** The band and index a card draws — `null` while "getting started". */
  commitmentLevel: CommitmentLevel | null;
  commitmentIndex: number | null;
  commitmentTrend: 'up' | 'stable' | 'down';
  /** Per-pillar scores (1–5) from the latest completed checkpoint; `null` with none. */
  pillarScores: Record<string, number> | null;
  /** The server's health per scored pillar; `null` with no completed checkpoint. */
  pillarHealth: Record<string, CarePillarHealth> | null;
  /** At-risk or flight-risk, live, and visible to this caller — Intervene reaches it. */
  needsIntervention: boolean;
  feedback: { pending: boolean; overdue: boolean; awaitingSelfEval: boolean };
}

/** One page of the Care list (`GET /business/care/talents`). */
export interface CareTalentsResponse {
  items: CareListRow[];
  total: number;
  page: number;
  limit: number;
  /** Who "Select all N" and the flight-risk shortcut would reach, over every match (D3). */
  selectable: { total: number; flightRisk: number };
  /** The sponsored talents who have not begun Care (Active view only). */
  notStarted: NotStartedList;
  truncated: boolean;
}

export interface CareTalentsParams {
  view: 'active' | 'archived';
  programId?: string;
  cohortId?: string;
  q?: string;
  commitment?: CareCommitmentFilter;
  pillar?: CarePillarFilter;
  page: number;
  limit: number;
}

export interface CareOverview {
  /** BL-066: the SPONSORED roster that shares Care (engagement is parallel). */
  talentCount: number;
  /** Talents left off this page because they withhold Care. */
  withheld: { care: number };
  /** BL-066: count who have STARTED Care (headline + "X of Y"). */
  startedCount?: number;
  /** The Archived tab's badge: every archived engagement at the Care stage. */
  archivedCount: number;
  /** Cohort mean Commitment Index across scored talents (null when none scored). */
  averageIndex: number | null;
  /** The canonical band of `averageIndex`; `null` when nobody is scored. */
  averageLevel: CommitmentLevel | null;
  /** Talents with enough signal to carry a real index (basis for `averageIndex` + distribution). */
  scoredCount: number;
  /** Talents who have begun Care but lack signal to score yet ("Getting started"). */
  gettingStartedCount: number;
  /** Cohort count of Care checkpoints currently in `overdue` status. */
  overdueCheckpoints?: number;
  /** Count of talents in each commitment band (scored talents only). */
  distribution: Record<CommitmentLevel, number>;
  /** Urgency-sorted intervention queue (at_risk + flight_risk). */
  atRiskQueue: Array<{
    id: string;
    /** The engagement — so the at-risk queue opens the right drawer. (W7.1) */
    journeyInstanceId: string | null;
    displayName: string;
    index: number;
    level: CommitmentLevel;
    topSignalKey: string | null;
  }>;
  /** Cohort pillar composites (avg of each pillar's latest 1–5 score per talent). */
  pillarComposites: Array<{
    code: string;
    averageScore: number;
    talentCount: number;
    /** The server's health for that average (`classifyPillarHealth`) — draw it. */
    health: CarePillarHealth;
  }>;
  /** The weakest pillar with data — the Insights focus; `null` with none. */
  weakestPillar: { code: string; averageScore: number } | null;
  /** The KPI strip over every started row — scored rows alone carry a band. */
  summary: {
    total: number;
    scored: number;
    gettingStarted: number;
    declining: number;
    needsIntervention: number;
    pendingFeedback: number;
    overdueFeedback: number;
    awaitingSelfEval: number;
  };
  /** Who needs acting on, most urgent first: the whole count and the first few. */
  interventionQueue: { total: number; talents: CareListRow[] };
  /** The commitment leaders — champions and the committed, highest index first. */
  leaders: CareListRow[];
  /** Every graded row of the roster (the Business Companion's overview tool). */
  talents: CareListRow[];
  /** The roster read hit its ceiling — the counts are over its first rows. */
  truncated: boolean;
  /**
   * BL-122: the EFFECTIVE Care "Visibility & Privacy" permissions for the
   * CURRENT caller, resolved server-side from the company config + the caller's
   * business role. The backend already REDACTS the gated data (risk signals +
   * the at-risk queue) when a flag is false — these booleans let the FE hide the
   * corresponding surfaces for UX (no empty shells). Optional so older API
   * responses (pre-BL-122) default to "show everything".
   */
  visibility?: {
    canSeeRiskSignals: boolean;
    canSeeDetailedFeedback: boolean;
  };
}

/**
 * The resolved EFFECTIVE Care strategy for a config scope (Country ← Company ←
 * Program ← Cohort) — mirrors `@tonnie/heroes` `CareStrategyConfig`. Drives the
 * inherited/effective config preview on Program + Cohort detail pages.
 */
export interface EffectiveCareStrategy {
  /** Pillar codes active/prioritised for this scope. */
  activePillars: string[];
  /** Subset of `activePillars` the talent cannot hide (company-mandated). */
  requiredPillars: string[];
  checkpointSchedule: {
    preMove: Array<{ type: string; milesReward: number }>;
    postArrival: Array<{ type: string; daysAfterArrival: number; milesReward: number }>;
  };
  feedbackCadence: string;
  interventionThresholds: { commitmentAtRisk: number; pillarConcern: number };
  /**
   * Resolved talent objectives that apply to this scope (catalog id + display
   * title + category). Mirrors `@tonnie/heroes` `CareResolvedObjective[]` that
   * `resolveForScope` returns. The Program/Cohort detail preview maps these into
   * the EffectiveCareCard's `{ id, text }` objective shape.
   */
  objectives?: { id: string; title: string; category?: string }[];
  /**
   * Business roles that provide objective feedback at the cadence (e.g.
   * `['admin','team_member']`). Resolved from the cascade.
   */
  feedbackProviders?: string[];
}

export interface EvidenceBadge {
  badgeId: string;
  code: string;
  name: string;
  rarity: string;
  iconUrl: string | null;
  /** Brand hex color for the shared passport badge card. */
  color: string | null;
  earnedAt: string | null;
}

export interface EvidenceStamp {
  stampId: string;
  code: string;
  name: string;
  category: string;
  /** Rarity + icon + miles so the shared passport stamp card renders faithfully. */
  rarity: string;
  icon: string | null;
  milesValue: number;
  earnedAt: string | null;
}

export interface EvidenceEvent {
  id: string;
  type: 'stamp' | 'badge';
  hero: string;
  /** Localizable label — render `t(titleKey, titleValues)` via resolveActivityLabel. */
  titleKey: string;
  titleValues?: Record<string, string | number>;
  talentId: string;
  talentName: string;
  miles: number;
  occurredAt: string | null;
}

export interface EvidenceOverview {
  totalTalents: number;
  totalBadges: number;
  /** R1 plan · S2 — count of unique stamp grants across the tenant. */
  totalStamps: number;
  totalMiles: number;
  /** Real achievement events (earned stamps/badges, true timestamps), newest-first.
   *  Capped to the recent feed; `totalEvents` carries the true total. */
  events?: EvidenceEvent[];
  /** True total achievement-event count (events[] is a capped recent slice). */
  totalEvents?: number;
  talents: Array<{
    id: string;
    displayName: string;
    email: string;
    pipelineStage: string;
    journeyId: string;
    /** Back-compat count. Prefer `badgesEarned.length`. */
    badges: number;
    miles: number;
    /** R1 plan · S2 — authoritative per-talent badge grants. */
    badgesEarned?: EvidenceBadge[];
    /** R1 plan · S2 — authoritative per-talent stamp grants. */
    stampsEarned?: EvidenceStamp[];
    /**
     * R1 plan · S3 — most-recent per-pillar Care scores. Keys are
     * pillar slugs (housing / work / social / wellbeing / financial /
     * family). Empty `{}` if no checkpoint with `pillarScores` exists
     * yet for this talent.
     */
    pillarScores?: Record<string, number>;
  }>;
  /** BL-061 — canonical stamp catalog (all active definitions) for the portfolio. */
  stampCatalog?: Array<{
    stampId: string;
    code: string;
    name: string;
    category: string;
    rarity: string;
    icon: string | null;
    milesValue: number;
  }>;
  /** BL-061 — canonical citizen-badge catalog (all active definitions). */
  badgeCatalog?: Array<{
    badgeId: string;
    code: string;
    name: string;
    rarity: string;
    icon: string | null;
    color: string | null;
    milesReward: number;
  }>;
  /**
   * BL-068 — canonical Journey Overview rollup (mutex phase), identical to the
   * Dashboard's, so the Evidence "Journey Overview" matches every other surface.
   */
  phaseCounts?: PhaseCounts;
  /** BL-068 — parallel engagement counts (mobility/adaptation/care active). */
  engagementCounts?: EngagementCounts;
  /** BL-068 — journeys at the `evidence` stage (drives the Vetting pass-rate). */
  completedCount?: number;
  /**
   * BL-070 — canonical per-pillar MESH averages (0–100) across the tenant's
   * scored talents, from the iqulture CheckUp assessment's `results.foundations`.
   * Replaces the client-derived breakdown that showed 0% (it read a non-existent
   * `assessment_type='mesh'` row).
   */
  /** Each pillar averaged over the talents scored on it; `null` when none was — never 0. */
  meshAverages?: {
    motivation: number | null;
    english: number | null;
    softSkills: number | null;
    hardSkills: number | null;
  };
  /** BL-070 — average iQulture score (0–100) across scored talents; `null` when none is. */
  iQultureAvg?: number | null;
  /** One badge per visible journey, by the server's rule; `unscored` = no CheckUp score. */
  iqultureBadgeCounts: Record<IQultureBadgeId | 'unscored', number>;
  /** Care pillar composites with the server's health — the same as the Care overview's. */
  pillarComposites: CareOverview['pillarComposites'];
  /** Average iQulture per month (UTC `YYYY-MM`) of the CheckUps calculated then; last six. */
  iqultureTrend: Array<{ monthKey: string; avgScore: number; assessmentCount: number }>;
  /** Journeys left out of each read above because the citizen withholds it. */
  withheld: { checkup: number; care: number; passport_stamps: number };
  /**
   * The portfolio passport, aggregated by the server once per TALENT: earned
   * counts per stamp / badge code, the rarity mix, the miles those items carry,
   * and how many talents sit on each citizen level (lowest first).
   */
  portfolio: {
    stampCounts: Record<string, number>;
    badgeCounts: Record<string, number>;
    rarityDistribution: Record<'common' | 'uncommon' | 'rare' | 'epic' | 'legendary', number>;
    milesFromAchievements: number;
    levels: Array<{ level: CitizenLevel; milesThreshold: number; talentCount: number }>;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 4 TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** @deprecated Use `PoolTalent` from the top of this file. */
export type PoolTalentProfile = PoolTalent;

export interface PoolSearchParams {
  q?: string;
  skills?: string[];
  /** Single location (legacy). Prefer `locations` for multi-city search. */
  location?: string;
  /** Multiple locations — OR-matched against a talent's city (BL-207). */
  locations?: string[];
  /** Any of these badges — the badge each result card shows. */
  iqultureBadges?: IQultureBadgeId[];
  /** Any of these job titles, exactly (the roles facet's values). */
  roles?: string[];
  /** The browse ordering — the server sorts the whole result set before paging. */
  sort?: PoolBrowseSort;
  page?: number;
  limit?: number;
}

/** @deprecated Use `PoolListResponse` from the top of this file. */
export type PoolSearchResult = PoolListResponse;

export interface BusinessDashboard {
  stages: Array<{ name: string; count: number }>;
  metrics: {
    totalTalents: number;
    activeJourneys: number;
  };
  /** BL-066 — canonical mutex phase rollup (pending_invite | vetting | sponsored),
   *  identical to the Pipeline/Cohort/Program model. Optional for older APIs. */
  phaseCounts?: PhaseCounts;
  /** BL-066 — canonical parallel engagement rollup (mobility/adaptation/care
   *  "started"). A sponsored talent contributes to 0..3. Optional for older APIs. */
  engagementCounts?: EngagementCounts;
  /** BL-068 — journeys at the `evidence` stage (drives the Vetting pass-rate funnel). */
  completedCount?: number;
  /** BL-072 — canonical average iQulture (0–100) across scored journeys; `null` when none is. */
  iQultureAvg?: number | null;
  /** Journeys whose CheckUp result is withheld — left out of `iQultureAvg`. */
  withheld: { checkup: number };
  /** The server's badge for that average; `null` when no journey is scored. */
  iQultureAvgBadge: IQultureBadgeId | null;
  /** Sponsored ÷ (vetting + sponsored), 0–100; `null` before anyone entered the funnel. */
  conversionRate: number | null;
  recentActivity: ActivityEvent[];
}

// R1 plan · D2 — exhaustive activity event union. Backend
// (`business.service.ts getActivity`) emits these discriminants.
// Frontends MUST handle every member or fall the call through an
// `assertNever` so we get a TypeScript build break the moment the
// backend adds a new one. Pre-fix the FE silently mapped unknown types
// to 'checkup' which hid backend additions for weeks.
export type ActivityEventType =
  | 'transition'
  | 'intervention'
  | 'protective_action'
  | 'intervention_scheduled'
  | 'document_uploaded'
  | 'task_reminder_sent';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  talentId: string;
  /**
   * Display name of the talent this event belongs to. The tenant-wide
   * feed mixes many talents, so the name is what makes a row
   * identifiable — render it alongside the label. Empty string when the
   * talent has no resolved name yet.
   */
  talentName?: string;
  /**
   * Localizable label — render `t(titleKey, titleValues)`. `titleValues` may
   * contain `__t:<key>__` placeholders the client resolves via `t()` first.
   * `title` is a transitional English fallback.
   */
  titleKey?: string;
  titleValues?: Record<string, string | number>;
  title?: string;
  detail?: string;
  reason?: string;
  createdAt: string;
}

export interface ActivityFeed {
  events: ActivityEvent[];
}

// ═══════════════════════════════════════════════════════════════════════════
// TONNIE SERVICE REQUESTS — "Ask Tonnie Services" modal
// ═══════════════════════════════════════════════════════════════════════════

export type TonnieServiceRequestStatus =
  'submitted' | 'in_review' | 'proposal_sent' | 'accepted' | 'declined' | 'closed';

export interface TonnieServiceRequest {
  id: string;
  scopes: string[];
  description: string;
  timeline: string | null;
  budgetRange: string | null;
  status: TonnieServiceRequestStatus;
  cohortId: string | null;
  programId: string | null;
  talentIds: string[];
  submittedBy: string;
  createdAt: string | null;
  proposalSentAt: string | null;
  closedAt: string | null;
}

export interface CreateTonnieServiceRequestInput {
  scopes: string[];
  description: string;
  timeline?: string;
  budgetRange?: string;
  cohortId?: string;
  programId?: string;
  talentIds?: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// SPONSORSHIP PRICING
// ═══════════════════════════════════════════════════════════════════════════

export interface SponsorshipPricing {
  activePlan: 'free' | 'enterprise';
  plans: Record<
    'free' | 'enterprise',
    {
      sponsorshipPerMonth: number;
      currency: string;
      /**
       * BL-022 discount decoration of the up-front TOTAL (rate × duration) —
       * what the checkout charges. Always sent by the API
       * (`BillingSponsorshipService.getSponsorshipPricing`).
       */
      pricing: import('./billing.service').PricingDto;
    }
  >;
  durationMonths: number;
}

// ═══════════════════════════════════════════════════════════════════════
// TALENT DETAIL — per-hero admin DTOs (mirrors backend types)
// ═══════════════════════════════════════════════════════════════════════

// ── L2 · F20 — `invitation`: an entry from the journey's own invitation history ──
export type TalentActivityKind = 'irr' | 'pipeline_transition' | 'message_sent' | 'invitation';
// ── end L2 ──
export type TalentActivityHero =
  'checkup' | 'mobility' | 'adaptation' | 'care' | 'evidence' | 'pipeline' | 'system';

/** What became of a message on the talent's timeline — the server's word. */
export type MessageActivityOutcome = 'sent' | 'not_delivered' | 'dispatched';

export interface TalentActivityEvent {
  id: string;
  kind: TalentActivityKind;
  hero: TalentActivityHero;
  occurredAt: string;
  /**
   * Localizable label — render `t(titleKey, titleValues)`. Every row carries
   * one; the API reads only labelled event types.
   */
  titleKey: string;
  titleValues?: Record<string, string | number>;
  /** English one-liner for the PDF talent report. Never rendered in the product. */
  summary: string;
  /** `unrecorded`: the source kept no actor — rendered as "actor not recorded" (D-3). */
  actor?: { type: 'user' | 'system' | 'citizen' | 'unrecorded'; id?: string | null };
  payload: Record<string, unknown>;
  /**
   * A message row's outcome, as the server decided it: the row's words and its
   * icon both follow it, so a message that never went out does not look like
   * one that did (BW-40). Set on `message_sent` rows only.
   */
  messageOutcome?: MessageActivityOutcome;
  /**
   * The journey this row belongs to, or `null` when it belongs to none.
   *
   * NULLABLE since W4.5. The API used to fall back to the talent's
   * first-sorting journey for any message with no journey link — which
   * production sends never have (0 of 102) — so every historical message named
   * a journey nobody had chosen. `null` is the honest answer; render the row
   * without a journey chip rather than inventing one.
   */
  journeyInstanceId: string | null;
}

export interface TalentActivityFeed {
  events: TalentActivityEvent[];
  journeyId: string | null;
  /** Strictly `nextCursor !== null`. Kept as a convenience for "more activity"
   *  hints; the cursor is what actually fetches the next page. */
  hasMore: boolean;
  /**
   * Opaque token for the next page, or `null` when this page is the last.
   * Pass it back as `getTalentActivity(id, { cursor })`. `null` cannot lie —
   * the server emits it only when its `limit + 1` overshoot came back short.
   */
  nextCursor: string | null;
  /**
   * Has the citizen granted this company the `irr` consent scope? (W8.D)
   *
   * `false` means every IRR-ledger row is ABSENT from `events` because of
   * that — not because nothing happened. The other two sources (pipeline
   * transitions and outbound messages) are the COMPANY's own actions and stay,
   * which is exactly why the flag is needed: a feed that still shows the
   * company's own moves while the citizen's hero activity has silently
   * vanished reads as "this person did nothing", and that is a claim about
   * them. Render the withheld notice above the list; never let a shorter feed
   * pass as a quieter citizen.
   *
   * TRUE only when EVERY visible journey grants it — a partially-entitled
   * talent is reported as withheld, because part of the feed IS missing.
   */
  irrEntitled: boolean;
}

/**
 * Per-talent CheckUp progress for the admin drawer. Gated on the `checkup`
 * consent scope (W8.D).
 *
 * NOT the full Brain results DTO — that is `getTalentCheckupResults`, whose
 * response has nowhere to hang a flag and therefore REFUSES with a 403
 * `CONSENT_SCOPE_WITHHELD` domain error when the scope is revoked.
 */
export interface TalentCheckupDetail {
  /**
   * Does the citizen share the CheckUp's RESULT with this company? (W8.D · D4)
   *
   * `false`: the score, badge and MESH below are `null` — withheld, never zero.
   * The PROCESS (status, dates, steps) is here either way: that a CheckUp is
   * done is the company's own pipeline. Render the withheld treatment for the
   * result, never a 0.
   */
  entitled: boolean;
  /** Why the result is, or is not, on screen. `shared` ⇔ `entitled`. */
  sharing: CheckupSharing;
  /** A score exists — the process fact, never the score. */
  scored: boolean;
  status: string | null;
  assessmentId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  calculatedAt: string | null;
  iqultureScore: number | null;
  iqultureBadge: IQultureBadgeId | null;
  /** The score is PROVISIONAL — a lower bound, some evidence missing (Option B). Draw it beside the score. */
  iqultureProvisional: boolean;
  /** What the complete evidence could reach; `null` unless provisional. */
  iqultureScoreMax: number | null;
  meshScores: Record<string, number | null> | null;
  /** Which steps are done — the company's pipeline, shown either way. */
  steps: {
    videosWatched: boolean;
    pitchVideoUploaded: boolean;
    languageTestsCompleted: boolean;
    motivationalAnswersCompleted: boolean;
    assistantChatCompleted: boolean;
  };
  missingSteps: string[];
  // ── L4 region: F26 calculation state ──
  /**
   * The calculation the journey's CheckUp is on, whoever started it — the
   * citizen or a company re-run — or `null` when none has been started on the
   * current assessment. Render "calculating"
   * from `isCheckUpCalculationRunning(calculation.status)` (`@tonnie/heroes`).
   */
  calculation: CheckUpCalculationState | null;
  // ── end L4 region ──
}

/**
 * Per-talent Mobility detail. MUST stay a byte-for-byte mirror of the backend
 * DTO returned by `TalentDetailService.getMobilityDetail`
 * (apps/platform-api/.../talents/talent-detail.service.ts). The server ships
 * raw DB-shaped fields (`trackCode`, `trackName`, `taskName`, `documentName`,
 * `validationStatus`) — do NOT invent client-only aliases here, or the FE reads
 * `undefined` at runtime (the original `trackType`/`name` drift crashed the
 * Mobility tab).
 */
export interface TalentMobilityDetail {
  /**
   * Has the citizen granted this company the `teleportal` consent scope?
   * (W8.D)
   *
   * DISTINCT from `householdEntitled`, and the two are independent on purpose:
   * the relocation plan is the work the company is paying for; the roster is
   * their children's passport numbers. `false` here means `setupCompleted` and
   * `overall` are `null` and `tracks` / `tasks` / `documents` / `milestones`
   * are `[]` BECAUSE of the revocation — render the withheld notice, never an
   * "everything at zero" plan.
   */
  entitled: boolean;
  /** True once the TelePortal setup assessment is complete (a setup profile
   *  exists). The primary "Mobility engaged" signal — the materialized plan
   *  tasks start as `pending`, so progress alone under-reports engagement.
   *  `null` when `entitled` is false: `false` would assert the citizen has not
   *  started, which is a claim about them rather than about their consent. */
  setupCompleted: boolean | null;
  /** Server-computed rollup — KPIs for the hero header (matches the citizen
   *  TelePortal hero + the Overview grid by construction). `null` when
   *  `entitled` is false — every field here is a count or a percentage, and a
   *  withheld plan reported as all-zero is the exact shape of "this citizen has
   *  done nothing". */
  overall: {
    overallProgress: number;
    tasksCompleted: number;
    tasksTotal: number;
    /** ready = validated | uploaded. */
    documentsReady: number;
    documentsTotal: number;
    /** total counts only milestones with tasksRequired > 0. */
    milestonesCompleted: number;
    milestonesTotal: number;
    blockedTracks: number;
    overdueTasks: number;
  } | null;
  /**
   * Has the citizen granted this company the `household` consent scope? (W3.3)
   *
   * `householdMembers: []` on its own is a FACTUAL CLAIM ABOUT A PERSON —
   * "this citizen has no family" — so an empty roster needs this flag beside
   * it to be readable as "withheld". Render the withheld state, never "No
   * household members".
   */
  householdEntitled: boolean;
  /** Household members for this journey (talent's household), ordered — the
   *  talent's OWN row first (`type: 'talent'`). W6.A: the applicant is a real
   *  roster row now, and a null `householdMemberId` means the whole household
   *  rather than the talent. Used to group tasks by member. Empty when
   *  `householdEntitled` is false. */
  householdMembers: Array<{
    id: string;
    name: string;
    type: string | null;
    relationship: string;
  }>;
  tracks: Array<{
    id: string;
    trackCode: string;
    trackName: string;
    progress: number;
    /** TeleportalTrackStatus: not_started | in_progress | completed | blocked | archived */
    status: string | null;
    startedAt: string | null;
    completedAt: string | null;
    dueDate: string | null;
    updatedAt: string | null;
  }>;
  tasks: Array<{
    id: string;
    trackId: string;
    trackCode: string;
    taskName: string;
    taskType: string;
    description: string | null;
    status: TeleportalTaskStatus;
    priority: TaskPriority;
    estimatedDuration: string | null;
    isBlocking: boolean;
    isRequired: boolean;
    dependsOn: string[];
    milesReward: number;
    orderIndex: number;
    location: string | null;
    /** talent | company | government | dual. */
    ownershipMode: string | null;
    /** household member this task is scoped to; null = the whole household. */
    householdMemberId: string | null;
    /**
     * True when this item is the APPLICANT'S OWN — their passport, their visa.
     *
     * Derived from the task's own metadata, never from `household_members`,
     * which this drawer may read only under the `household` consent scope. It
     * therefore travels even when the roster is withheld, so the talent's own
     * obligations are never filed under "household member — name withheld".
     */
    householdMemberIsSelf: boolean;
    dueDate: string | null;
    completedAt: string | null;
  }>;
  documents: Array<{
    id: string;
    trackId: string | null;
    taskId: string;
    documentName: string;
    documentType: string;
    description: string | null;
    /** TeleportalDocValidationStatus: pending | uploaded | validated | rejected | missing */
    validationStatus: string;
    validatedAt: string | null;
    validationNotes: string | null;
    expiryDate: string | null;
    requiresCertification: boolean;
    certificationType: string | null;
    validityDays: number | null;
    location: string | null;
    /** household member this document belongs to; null = the whole household. */
    householdMemberId: string | null;
    /**
     * True when this item is the APPLICANT'S OWN — their passport, their visa.
     *
     * Derived from the task's own metadata, never from `household_members`,
     * which this drawer may read only under the `household` consent scope. It
     * therefore travels even when the roster is withheld, so the talent's own
     * obligations are never filed under "household member — name withheld".
     */
    householdMemberIsSelf: boolean;
  }>;
  milestones: Array<{
    id: string;
    track: string;
    title: string;
    targetDate: string | null;
    completedAt: string | null;
    tasksRequired: number;
    tasksCompleted: number;
    orderIndex: number;
  }>;
}

/**
 * Per-talent Adaptation (QultureUp) detail. Mirrors the backend DTO from
 * `TalentDetailService.getAdaptationDetail`, which delegates to the canonical
 * `QultureUpService` the citizen hero reads. `worlds[]` are PER-WORLD aggregates
 * (progress % + mission counts) and `overall` is a server-side rollup, so the
 * admin header matches the citizen QultureUp hero by construction.
 */
export interface TalentAdaptationDetail {
  /**
   * Has the citizen granted this company the `qultureup` consent scope?
   * (W8.D)
   *
   * `false` means WITHHELD: `overall`, `miles` and `streakDays` are `null` and
   * `worlds` / `recommendedNext` are `[]` BECAUSE of that. A zeroed rollup
   * would read "this citizen has learned nothing" — a claim about them built
   * out of a permission we do not hold.
   */
  entitled: boolean;
  /**
   * Server-side rollup so the admin Adaptation header matches the citizen
   * QultureUp hero by construction. `overallProgress` mirrors the citizen
   * banner — `round(sum(missionsCompleted)/sum(missionsTotal)*100)` over the
   * full worlds catalog. `null` when `entitled` is false.
   */
  overall: {
    overallProgress: number;
    level: string | null;
    levelScore: number | null;
    /** When the knowledge assessment was completed (citizen "Assessed on {date}"). */
    assessedAt: string | null;
    missionsCompleted: number;
    missionsTotal: number;
    /** Learning tracks rolled up across worlds — citizen QultureUp "Tracks X/Y". */
    tracksCompleted: number;
    tracksTotal: number;
    worldsCompleted: number;
    streakDays: number;
    /** Momentum signals (citizen getProgress) for the deep-dive trend. */
    longestStreak: number;
    weeklyMissionsCompleted: number;
    /** Per-day missions completed over the last 7 days (citizen "This Week"). */
    weeklyProgress: number[];
    totalTimeSpentMinutes: number;
    lastActivityDate: string | null;
  } | null;
  worlds: Array<{
    id: string;
    worldCode: string;
    name: string;
    /** World blurb (citizen world card). */
    description: string;
    iconUrl: string | null;
    /** completed | in_progress | not_started | locked */
    status: string;
    /** 0–100 per-world progress. */
    progress: number;
    missionsCompleted: number;
    missionsTotal: number;
    tracksCompleted: number;
    tracksTotal: number;
    level: string | null;
    isLocked: boolean;
    /** Read-only tracks → missions tree (citizen Tracks & Missions tab). */
    tracks: Array<{
      id: string;
      name: string;
      description: string;
      level: string | null;
      status: string;
      progress: number;
      isLocked: boolean;
      missionsCompleted: number;
      missionsTotal: number;
      missions: Array<{
        id: string;
        title: string;
        difficulty: string;
        status: string;
        isLocked: boolean;
      }>;
    }>;
  }>;
  /** `null` when withheld — never `0`, which reads as "earned nothing". */
  miles: number | null;
  /** `null` when withheld — never `0`, which reads as "never showed up". */
  streakDays: number | null;
  recommendedNext: Array<{ worldCode: string; missionCode: string; title: string }>;
}

/** One row of the cross-talent Care intervention follow-up inbox. */
export interface CareInterventionInboxRow {
  id: string;
  title: string;
  /** An Adaptation coaching record's action — name it by this (D1); `null` for any other. */
  coachingAction: 'encourage' | 'challenge' | 'checkin' | 'celebrate' | 'remind' | null;
  type: string;
  status: string;
  priority: string;
  talentId: string;
  talentName: string;
  assignedTo: string | null;
  dueAt: string | null;
  /** Late — only while the interaction is still open. */
  overdue: boolean;
  createdAt: string;
}

/** One talent's interactions in the WHOLE slice — a grouped list's header. */
export interface CareInboxTalentTotal {
  talentId: string;
  total: number;
  overdue: number;
}

/**
 * The inbox: one page of rows, and the server's counts over the WHOLE slice —
 * draw a count from `total` / `overdue` / `talentTotals`, never from a length.
 */
export interface CareInterventionInbox {
  items: CareInterventionInboxRow[];
  total: number;
  overdue: number;
  page: number;
  limit: number;
  talentTotals: CareInboxTalentTotal[];
}

/** What one inbox page asks for (`careInboxQuerySchema` on the server). */
export interface CareInboxParams {
  status: 'open' | 'done' | 'all';
  /** Only the interactions delegated to the caller. */
  mine?: boolean;
  /** The talent's name or email contains this. */
  q?: string;
  order: CareInboxOrder;
  page: number;
  limit: number;
}

/** One comment in a Care interaction's internal collaboration thread. */
export interface CareInterventionComment {
  id: string;
  content: string;
  createdBy: string;
  createdAt: string;
}

/** Full detail for one Care interaction (the detail drawer). */
export interface CareInterventionDetail {
  id: string;
  title: string;
  /** An Adaptation coaching record's action — name it by this (D1); `null` for any other. */
  coachingAction: 'encourage' | 'challenge' | 'checkin' | 'celebrate' | 'remind' | null;
  type: string;
  status: string;
  priority: string;
  talentId: string;
  talentName: string;
  assignedTo: string | null;
  scheduledAt: string | null;
  description: string | null;
  locationOrLink: string | null;
  outcome: string | null;
  ata: string | null;
  overdue: boolean;
  createdAt: string;
}

export interface TalentCareDetail {
  /**
   * Has the citizen granted this company the `care` consent scope? (W8.D)
   *
   * DISTINCT from `checkinsEntitled`, and independent in both directions: a
   * citizen may keep the company in their Care programme while taking the
   * daily mood diary back, or revoke the programme and leave nothing but the
   * company's own nudges.
   *
   * `false` ⇒ `overall`, `commitment` and `baseline` are `null` and
   * `commitmentHistory` / `pillars` / `checkpoints` are `[]` BECAUSE of the
   * revocation. `interventions` STAYS populated — it is the COMPANY's own
   * record of the nudges IT sent, and emptying it would make the panel read
   * "we never reached out", a false claim about the company's own audit trail.
   */
  entitled: boolean;
  /**
   * Server-side rollup sourced from the canonical `getWellbeingSummary` — the
   * SAME computation the citizen Care hero uses — so `wellbeingScore`
   * (= citizen `thermometerScore`), `phase`, `averageMood` and `settlementScore`
   * match the citizen surface by construction. `null` when the `care` scope is
   * withheld (W8.D) — never an all-zero record.
   */
  overall: {
    /**
     * 50% mood + 50% settlement. `null` when check-ins are withheld (W3.3):
     * with the mood half missing the composite would silently become the
     * settlement half alone under the same label. Also `null` with no mood or
     * settlement data yet — the drawer tells the two apart by `checkinsEntitled`.
     */
    wellbeingScore: number | null;
    phase: 'pre_move' | 'settling_in' | 'adapting' | 'thriving';
    /** Check-in derived. `null` when check-ins are withheld, or none is in the window — never 0. */
    averageMood: number | null;
    /** Check-in derived. `null` when check-ins are withheld — never 'stable'. */
    moodTrend: 'up' | 'down' | 'stable' | null;
    /** The latest checkpoint's pillar average (1–5); `null` before one is scored — never 0. */
    settlementScore: number | null;
    settlementTrend: 'up' | 'down' | 'stable';
    checkpointsCompleted: number;
    checkpointsTotal: number;
    /** Progress through Care (0–100), the server's — draw it, never recompute it. */
    progressPct: number;
  } | null;
  /**
   * Commitment Index breakdown — the company's retention/"protect the
   * investment" score. Null during cold start (insufficient signal — before
   * ≥1 completed checkpoint OR ≥5 distinct check-in days); when present,
   * `index`/`level`/`riskLevel` are always real, though individual
   * `components` may still be null until measurable. Also `null` when the
   * `care` scope is withheld (W8.D) — and `null` is already the cold-start
   * shape, so `entitled` is the only thing that tells the two apart.
   */
  commitment: {
    index: number;
    level: CommitmentLevel;
    riskLevel: 'low' | 'medium' | 'high';
    trend: 'improving' | 'stable' | 'declining';
    components: CommitmentComponents;
    riskSignals: CommitmentRiskSignal[];
    lastCalculated: string | null;
  } | null;
  /**
   * Commitment Index time-series for the admin sparkline (BL-115) — answers
   * "trending down since when?". Each point is the index AT a prior calculation
   * (oldest-first); cold-start rows with no index are omitted. Empty or a single
   * point until enough history accrues — render an empty state below 2 points.
   * BL-122: empty array when the caller's role isn't permitted to see Risk
   * Signals (history is a risk-adjacent signal, gated the same way).
   * Also `[]` when the `care` scope is withheld (W8.D).
   */
  commitmentHistory: Array<{ date: string; index: number }>;
  /** Baseline from the active Care assessment (focus areas + concerns).
   *  `null` when the `care` scope is withheld (W8.D) — the concerns are prose
   *  the citizen wrote down, and an empty baseline reads as "they raised
   *  none". */
  baseline: {
    selectedPillars: string[];
    concerns: string[];
    completedAt: string | null;
  } | null;
  /** `[]` when the `care` scope is withheld (W8.D) — never "this citizen
   *  scored no pillars". */
  pillars: Array<{
    code: string;
    name: string;
    selfScore: number | null;
    companyScore: number | null;
    trend: 'up' | 'down' | 'stable' | null;
  }>;
  /** `[]` when the `care` scope is withheld (W8.D) — never "completed none". */
  checkpoints: Array<{
    id: string;
    type: string;
    name: string;
    status: string;
    scheduledFor: string | null;
    completedAt: string | null;
    pillarScores: Record<string, number> | null;
    milesReward: number;
  }>;
  /**
   * Aggregate check-in signals ONLY. Privacy boundary: the company never sees
   * individual daily mood scores or free-text notes — only frequency, streak,
   * and trend direction.
   */
  /**
   * May THIS viewer see the risk-adjacent half of the Care record? (BL-122 · W8.D)
   *
   * A ROLE gate, not a consent gate, and the two are independent: `entitled`
   * says what the CITIZEN permits this company, this says what the COMPANY
   * permits this member of it. Both can withhold the same field for different
   * reasons and a reader needs to be told which.
   *
   * `false` ⇒ `commitmentHistory` is `[]` and `commitment.riskSignals` is `[]`
   * BECAUSE of the role, not because the citizen is doing fine. Without this
   * flag the drawer renders a Team Member "no risk signals" for a talent the
   * company is actively worried about — which is the same class of falsehood
   * the consent flags exist to stop, arriving through the other door. The
   * Companion already computes it separately; the drawer had no way to.
   */
  riskSignalsVisible: boolean;
  /**
   * Has the citizen granted this company the `care_checkins` consent scope?
   * (W3.3) `false` ⇒ `checkinsSummary` is null and the check-in-derived halves
   * of `overall` are null.
   */
  checkinsEntitled: boolean;
  /**
   * `null` when withheld (W3.3). An all-zero summary is a different claim —
   * "this citizen never checked in" — and is the shape a wellbeing score gets
   * computed from.
   */
  checkinsSummary: {
    totalCheckins: number;
    last30Days: number;
    currentStreak: number;
    moodTrend: 'up' | 'down' | 'stable' | null;
    lastCheckinAt: string | null;
  } | null;
  /**
   * Business-only nudges — no citizen equivalent.
   *
   * Deliberately OUTSIDE both consent gates and still populated when
   * `entitled` is false (W8.D): it is the COMPANY's own record of what IT
   * sent. Returning it empty would make the panel read "we never reached out"
   * and invite an operator to do it again.
   */
  interventions: Array<{
    id: string;
    type: string;
    title: string;
    /** An Adaptation coaching record's action — name it by this (D1); `null` for any other. */
    coachingAction: 'encourage' | 'challenge' | 'checkin' | 'celebrate' | 'remind' | null;
    status: string;
    priority: string;
    scheduledAt: string | null;
    completedAt: string | null;
    schedule: Record<string, unknown> | null;
    assignedTo: string | null;
    createdAt: string;
  }>;
}

export interface TalentEvidenceDetail {
  /**
   * Has the citizen granted this company the `passport_stamps` consent scope?
   * (W3.3)
   *
   * `false` means WITHHELD: every field below is null or empty because of
   * that, NOT because the citizen earned nothing. Hide the KPIs — do not
   * substitute zeros, and do not feed these nulls into an average as 0. The
   * nulls exist precisely so they cannot be mistaken for data.
   */
  entitled: boolean;
  /** Miles earned on THIS journey. `null` = withheld (never 0 — see `entitled`). */
  miles: number | null;
  /** Miles earned in the last 7 days (citizen Passport "Miles this week"). `null` = withheld. */
  milesThisWeek: number | null;
  /** The citizen's level on `miles`, computed by the server — draw it. `null` = withheld. */
  level: CitizenLevelLadder | null;
  /**
   * Total active stamps / citizen badges (the DENOMINATORS of "earned / total").
   * `null` = withheld — rendering `0 / 0` reads as a broken KPI, not a
   * withheld one, so hide the KPI instead.
   */
  stampsTotal: number | null;
  badgesTotal: number | null;
  badges: Array<{
    id: string;
    code: string;
    name: string;
    description: string | null;
    earnedAt: string;
    /** Badge rarity (common | rare | epic | legendary …), or null. */
    rarity: string | null;
  }>;
  stamps: Array<{
    id: string;
    code: string;
    name: string;
    earnedAt: string;
  }>;
  achievements: Array<{
    id: string;
    title: string;
    progress: number;
    completed: boolean;
  }>;
}

export function createBusinessService(client: TonnieClient): BusinessService {
  return new BusinessService(client);
}
